import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select2Dropdown from '../common/Select2Dropdown';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import { exportExcelSection, exportPDFSection } from '../common/ExportUtils';
import { openFilePreview } from '../common/FilePreviewModal';
import { toast } from 'react-toastify';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import
import { getAuthHeaders } from '../../auth';
import JobForm from '../documentation/JobForm';
import DepositReport from './DepositReport';
import TableScrollContainer from '../common/TableScrollContainer';

const API_BASE = '/api/documentation/jobs';
const STAFF_API = '/api/admin/staff/?active=true';
const LOCATIONS_API = '/api/masters/drop-off-locations/';

const DO_STATUS_OPTIONS = [
  'DO RECEIVED, AWAITING DROP-OFF',
  'WAITING CLIENT CONFIRMATION',
  'WAITING CLIENT PAYMENT',
  'WAITING VESSEL ARRIVAL',
  'WAITING RENEWED DO',  
  'WAITING MASTER DO',
  'WAITING DEM CONF',
  'WAITING INVOICE',
  'TO COLLECT DO',
  'WAITING DUTY',
  'WAITING PRV',
  'TO LODGE DO',
  'WAITING DO',
  'DO ON HOLD',
  'DO RECEIVED',
  'TO PAY'
];

const BL_TYPE_OPTIONS = ['NO OBL','SWB', 'TLX', 'OBL'];

const NOMINATION_STATUS_OPTIONS = [
  { id: '', text: 'PENDING' },
  { id: 'LATE', text: 'LATE' },
  { id: 'REJECTED', text: 'REJECTED' },
  { id: 'SHIPPER_NOMINATION', text: 'SHIPPER NOMINATION' },
  { id: 'ACCEPTED', text: 'ACCEPTED' }
];

const NOMINATION_STATUS_COLORS = {
  '': '#f0ad4e',
  'LATE': '#dc3545',
  'REJECTED': '#6c757d',
  'SHIPPER_NOMINATION': '#17a2b8',
  'ACCEPTED': '#28a745',  
  'ALL': '#007bff',
};

// Helper to format date for input[type=date] which needs YYYY-MM-DD
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr.slice(0, 10);
  }
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
  } catch (e) {
    return '';
  }
};

// Convert dd/mm/yyyy or yyyy-mm-dd into a yyyy-mm-dd string for consistent comparison/storage
const normalizeDateInput = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr !== 'string') return dateStr;
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  const ukMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ukMatch) return `${ukMatch[3]}-${ukMatch[2]}-${ukMatch[1]}`;
  return dateStr;
};

const fmtDateShort = d => {
  if (!d) return '';
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const day = String(dt.getDate()).padStart(2, '0');
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const year = dt.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return d;
  }
};
const toUpper = v => (v || '').toString().toUpperCase();
// True when the given date is tomorrow, today, or already past (day precision).
const isExpiringSoon = dateStr => {
  if (!dateStr) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  if (isNaN(d.getTime())) return false;
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  return diff <= 1;
};
const isReleasedJob = j => String(j?.port_remarks || '').toUpperCase().includes('RELEASED');
const DO_RECEIVED = 'DO RECEIVED';
const DO_RECEIVED_AWAITING_DROPOFF = 'DO RECEIVED, AWAITING DROP-OFF';
const PRIMARY_LETTER_TYPES = new Set(['AUTHORIZATION', 'DO ATTACHMENT', 'DROPOFF ATTACHMENT']);

const getBlValue = j => {
  if (j.bl_value) return j.bl_value;
  const isLcl = (j.shipment_type || '').toString().toUpperCase() === 'LCL' || 
                (j.cargo_type || '').toString().toUpperCase() === 'LCL';
  if (isLcl) {
    return j.hbl_so_no || j.hblSoNo || j.bl_no || '-';
  }
  return j.mbl_no || j.mblNo || j.hbl_so_no || j.bl_no || '-';
};

const renderBlNumber = (job, onClickHandler) => {
  const isLcl = (job.shipment_type || '').toString().toUpperCase() === 'LCL' || 
                (job.cargo_type || '').toString().toUpperCase() === 'LCL';
  const title = isLcl ? `MBL: ${job.mbl_no || job.mblNo || '-'}` : undefined;
  const blText = toUpper(getBlValue(job));

  if (onClickHandler) {
      return <a href="#" onClick={(e) => { e.preventDefault(); onClickHandler(job); }} title={title}>{blText}</a>;
  }
  
  return <span title={title}>{blText}</span>;
};

export default function ShippingReport() {
  const { hasPermission } = usePermissions(); // 2. Get the permission checker function
  const [dropoffExpirySaveMode, setDropoffExpirySaveMode] = useState('staged');
  const [dropoffExpiryTarget, setDropoffExpiryTarget] = useState('dialog');
  const canEditShippingReport = hasPermission('SHIPPING REPORTS', 'can_edit');
  const canEditDropoffExpiryModal = canEditShippingReport || (dropoffExpirySaveMode === 'direct' && hasPermission('SHIPPING REPORTS', 'can_delete'));
  const [jobs, setJobs] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]); // State for pending jobs
  const [loading, setLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false); // Loading state for pending
  const [searchBy, setSearchBy] = useState('IDF');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState({ by: 'IDF', term: '' });
  const [slUserFilter, setSlUserFilter] = useState('');
  const [shippingStaff, setShippingStaff] = useState([]);
  const [dropOffLocations, setDropOffLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('pending_reports'); // 'pending_reports', 'shipping', 'do_received', 'consignee_nomination', or 'deposits'
  const [deposits, setDeposits] = useState([]);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [swapFromUser, setSwapFromUser] = useState('');
  const [swapToUser, setSwapToUser] = useState('');
  const [swappingUsers, setSwappingUsers] = useState(false);
  
  // Consignee Nomination State
  const [nominationJobs, setNominationJobs] = useState([]);
  const [nominationLoading, setNominationLoading] = useState(false);
  const [nominationStatusFilter, setNominationStatusFilter] = useState(['']);
  const [showMDAlert, setShowMDAlert] = useState(false);

  // JobForm modal state
  const [jobFormModal, setJobFormModal] = useState({ isOpen: false, job: null });

  // DO Received Pagination
  const [doReceivedPage, setDoReceivedPage] = useState(1);
  const [doReceivedPageSize, setDoReceivedPageSize] = useState(25);

  // Centralized editing dialog state
  const [shippingDetailsDialog, setShippingDetailsDialog] = useState({
    open: false,
    job: null,
    tempDOStatuses: [],
    tempBLType: '',
    tempSLUserId: '',
    letterFile: null,
    letterAttachmentPreviewUrl: '',
    doReceivedDate: '',
    doExpiryDate: '', // Add this line
    dropOffExpiryDate: '',
    dropOffDate: '',
    dropOffLocation: '',    
    doAttachmentFile: null,
    doAttachmentPreviewUrl: '',
    dropOffAttachmentFile: null,
    dropoffAttachmentPreviewUrl: '',
    additionalLetterType: '',
    additionalLetterFile: null,
    // New DO entry fields (separate from existing history)
    newDoEntry: {
      doReceivedDate: '',
      doExpiryDate: '',
      dropOffDate: '',
      dropOffExpiryDate: '',
      dropOffLocation: '',
      doAttachmentFile: null,
      doAttachmentPreviewUrl: '',
      dropOffAttachmentFile: null,
      dropoffAttachmentPreviewUrl: '',
      usePreviousDO: false,
      usePreviousDropoff: false,
    },
    saving: false,
  });

  // Per-container drop-off expiry date modal
  const [showDropoffExpiryModal, setShowDropoffExpiryModal] = useState(false);
  const [dropoffExpiryContainers, setDropoffExpiryContainers] = useState([]);
  const [selectedDropoffContainerIds, setSelectedDropoffContainerIds] = useState([]);
  const [bulkDropoffExpiryDate, setBulkDropoffExpiryDate] = useState('');
  const [savingDropoffExpiry, setSavingDropoffExpiry] = useState(false);

  // DO History edit/delete state
  const [editingDoRecord, setEditingDoRecord] = useState(null);
  const [doRecordEditDraft, setDoRecordEditDraft] = useState({});
  const [doRecordSaving, setDoRecordSaving] = useState(false);
  const [deletingDoRecord, setDeletingDoRecord] = useState(null);
  const [blLoading, setBlLoading] = useState(false);
  const [showFreeDaysConfirm, setShowFreeDaysConfirm] = useState(false);

  // Determine which DO statuses to show in the dialog based on BL Type
  const dialogDoStatusOptions = useMemo(() => {
    const dialogCargoType = (shippingDetailsDialog.job?.cargo_type || shippingDetailsDialog.job?.shipment_type || '').toString().toUpperCase();
    const isLcl = dialogCargoType.includes('LCL') || dialogCargoType.includes('VEHICLE');

    if (shippingDetailsDialog.tempBLType === 'NO OBL') {
      return ['TO PAY', 'WAITING INVOICE', 'WAITING VESSEL ARRIVAL', 'WAITING CLIENT PAYMENT', 'INVOICE RECEIVED', 'PAID, WAITING BL'];
    }

    if (isLcl) {
      return DO_STATUS_OPTIONS.filter(status => status !== DO_RECEIVED_AWAITING_DROPOFF);
    }

    return DO_STATUS_OPTIONS;
  }, [shippingDetailsDialog.tempBLType, shippingDetailsDialog.job]);

  const hasDoDetailStatusSelected =
    (shippingDetailsDialog.tempDOStatuses ?? []).includes(DO_RECEIVED) ||
    (shippingDetailsDialog.tempDOStatuses ?? []).includes(DO_RECEIVED_AWAITING_DROPOFF);
  const isLclCargo = ((shippingDetailsDialog.job?.cargo_type || shippingDetailsDialog.job?.shipment_type || '').toString().toUpperCase().includes('LCL')) ||
    ((shippingDetailsDialog.job?.cargo_type || shippingDetailsDialog.job?.shipment_type || '').toString().toUpperCase().includes('VEHICLE'));
  const showDropoffFields = !isLclCargo && !(shippingDetailsDialog.tempDOStatuses ?? []).includes(DO_RECEIVED_AWAITING_DROPOFF);

  const authHeaders = (json = true) => {
    const token = localStorage.getItem('kk_token') || localStorage.getItem('token');
    return {
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const enrichJob = j => ({
    ...j,
    eta: j.eta || j.vessel_details?.eta || '',
    ata:
      j.ata ||
      j.arrival_date ||
      j.vessel_details?.arrival_date ||
      j.vessel_details?.ata ||
      '',
    etb: j.etb || j.vessel_details?.etb || '',
    atb:
      j.atb ||
      j.berthing_date ||
      j.vessel_details?.berthing_date ||
      j.vessel_details?.atb ||
      '',
    manifest_ready_date:
      j.manifest_ready_date || '',
    do_statuses: Array.isArray(j.do_statuses)
      ? j.do_statuses
      : (j.do_status || j.doStatus || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
  });

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedSearch.term.trim()) {
        params.set('search', appliedSearch.term.trim());
        params.set('search_by', appliedSearch.by === 'Customer' ? 'CUSTOMER' : appliedSearch.by);
      }
      const qs = params.toString();
      let url = `${API_BASE}/shipping-report/${qs ? `?${qs}` : ''}`;
      let res = await fetch(url, { headers: authHeaders(false) });
      if (res.status === 404) {
        url = `${API_BASE}/?shipping_report=1${qs ? `&${qs}` : ''}`;
        res = await fetch(url, { headers: authHeaders(false) });
      }
      if (!res.ok) throw new Error('Failed to load shipping report');
      let data = await res.json();
      if (!Array.isArray(data)) data = [];

      let filtered = data.map(enrichJob);

      filtered.sort((a, b) => {
        const aM = a.manifest_ready_date || '';
        const bM = b.manifest_ready_date || '';
        const aT = a.atb || '';
        const bT = b.atb || '';
        if (bM !== aM) return bM > aM ? 1 : -1;
        return bT > aT ? 1 : bT < aT ? -1 : 0;
      });

      setJobs(filtered);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch]);

  const handleAcknowledgeSl = async (job) => {
    if (!job || !job.manifest_ready_date || job.sl_acknowledged) {
      return; // Don't do anything if not needed
    }

    try {
      const res = await fetch(`${API_BASE}/${job.id}/acknowledge-sl/`, {
        method: 'POST',
        headers: authHeaders(false),
      });
      if (!res.ok) {
        throw new Error('Failed to acknowledge.');
      }
      // Update the job in the local state to stop flashing immediately
      setJobs(currentJobs =>
        currentJobs.map(j =>
          j.id === job.id ? { ...j, sl_acknowledged: true } : j
        )
      );
      toast.success(`Acknowledged job ${job.id}.`);
    } catch (e) {
      console.error(e);
      toast.error('Could not acknowledge the item.');
    }
  };

  const openJobFormModal = async (job) => {
    try {
      const res = await fetch(`${API_BASE}/${job.id}/`, { headers: authHeaders(false) });
      if (!res.ok) {
        toast.error('Failed to load job details');
        return;
      }
      const data = await res.json();
      setJobFormModal({ isOpen: true, job: data });
    } catch (e) {
      console.error(e);
      toast.error('Error loading job');
    }
  };

  const closeJobFormModal = (refresh = false) => {
    setJobFormModal({ isOpen: false, job: null });
    if (refresh) {
      fetchJobs();
      fetchPendingJobs();
      fetchNominationJobs();
    }
  };

  const fetchPendingJobs = useCallback(async () => {
    setPendingLoading(true);
    try {
        const params = new URLSearchParams();
        if (appliedSearch.term.trim()) {
          params.set('search', appliedSearch.term.trim());
          params.set('search_by', appliedSearch.by === 'Customer' ? 'CUSTOMER' : appliedSearch.by);
        }
        const qs = params.toString();
        const res = await fetch(`${API_BASE}/shipping-pending-report/${qs ? `?${qs}` : ''}`, { headers: authHeaders(false) });
        if (!res.ok) throw new Error('Failed to load pending shipping report');
        let data = await res.json();
        if (!Array.isArray(data)) data = [];

        let filtered = data.map(enrichJob);

        filtered.sort((a, b) => {
          const aM = a.manifest_ready_date || '';
          const bM = b.manifest_ready_date || '';
          const aT = a.atb || '';
          const bT = b.atb || '';
          if (bM !== aM) return bM > aM ? 1 : -1;
          return bT > aT ? 1 : bT < aT ? -1 : 0;
        });

        setPendingJobs(filtered);
    } catch (e) {
        console.error(e);
        toast.error('Could not load pending jobs.');
    } finally {
        setPendingLoading(false);
    }
  }, [appliedSearch]);

  const fetchNominationJobs = useCallback(async () => {
    setNominationLoading(true);
    try {
        const res = await fetch(`${API_BASE}/consignee-nomination-report/`, { headers: authHeaders(false) });
        if (!res.ok) throw new Error('Failed to load consignee nomination report');
        let data = await res.json();
        if (!Array.isArray(data)) data = [];
        setNominationJobs(data);
    } catch (e) {
        console.error(e);
        toast.error('Could not load consignee nomination jobs.');
    } finally {
        setNominationLoading(false);
    }
  }, []);

  const fetchDeposits = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/shipping-line-payables/deposit-report/', { headers: authHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      setDeposits(data.rows || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchShippingStaff = useCallback(async () => {
    try {
      const res = await fetch(STAFF_API, { headers: authHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      const filtered = (Array.isArray(data) ? data : []).filter(s =>
        (s.department_name || '').toUpperCase().includes('SHIPPING') && s.user_id != null
      );
      setShippingStaff(filtered);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchDropOffLocations = useCallback(async () => {
    try {
      const res = await fetch(LOCATIONS_API, { headers: authHeaders(false) });
      if (!res.ok) return;
      const data = await res.json();
      setDropOffLocations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Failed to fetch drop-off locations', e);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    fetchPendingJobs();
  }, [fetchPendingJobs]);

  useEffect(() => {
    fetchShippingStaff();
    fetchDropOffLocations();
    fetchNominationJobs();
  }, [fetchShippingStaff, fetchDropOffLocations, fetchNominationJobs]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);


  const enforceDoReceivedRule = list => {
    if (!Array.isArray(list)) return [];
    if (list.includes(DO_RECEIVED)) return [DO_RECEIVED];
    if (list.includes(DO_RECEIVED_AWAITING_DROPOFF)) return [DO_RECEIVED_AWAITING_DROPOFF];
    return list;
  };

  const handleDoStatusChange = (newStatuses) => {
    const { job, tempBLType, tempSLUserId, letterFile } = shippingDetailsDialog;
    const isLclCargo = ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('LCL')) ||
      ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('VEHICLE'));
    const hadAwaitingDropoffAlready = Array.isArray(job?.do_statuses) && job.do_statuses.includes(DO_RECEIVED_AWAITING_DROPOFF);
    let normalizedStatuses = newStatuses.map(v => v.toUpperCase());

    if (isLclCargo && normalizedStatuses.includes(DO_RECEIVED_AWAITING_DROPOFF) && !hadAwaitingDropoffAlready) {
      toast.warn('DO RECEIVED, AWAITING DROP-OFF is not allowed for LCL/VEHICLE cargo.');
      return;
    }

    if (isLclCargo && hadAwaitingDropoffAlready && normalizedStatuses.length > 1) {
      normalizedStatuses = normalizedStatuses.filter(status => status !== DO_RECEIVED_AWAITING_DROPOFF);
    }

    const isChangingToDoReceived = normalizedStatuses.includes(DO_RECEIVED);
    const isChangingToAwaitingDropoff = normalizedStatuses.includes(DO_RECEIVED_AWAITING_DROPOFF);

    if (isChangingToDoReceived || isChangingToAwaitingDropoff) {
      const authLetter = authLetterFor(job);
      const hasAuthLetter = !!authLetter || !!letterFile;
      if (!hasAuthLetter) {
        toast.warn('Please ensure an Authorization Letter is uploaded before setting status to DO RECEIVED.');
        return; // Prevent change
      }
      if (!tempBLType) {
        toast.warn('Please select a BL Type before setting status to DO RECEIVED.');
        return; // Prevent change
      }
      if (!tempSLUserId) {
        toast.warn('Please assign an SL User before setting status to DO RECEIVED.');
        return; // Prevent change
      }
    }
    
    setShippingDetailsDialog(d => ({ ...d, tempDOStatuses: enforceDoReceivedRule(normalizedStatuses) }));
  };

  const handleNominationStatusChange = async (jobId, newStatus) => {
      const statusToSend = newStatus || '';
      
      // Permission check
      if (!hasPermission('SHIPPING REPORTS', 'can_edit')) {
            toast.error("Permission denied.");
            return;
      }
      if (statusToSend === 'LATE') {
          // Alert user to inform MD
          setShowMDAlert(true);
      }

      try {
          // Optimistic update logic could be added here, but for now we wait for server
          const res = await fetch(`${API_BASE}/${jobId}/update_nomination_status/`, {
              method: 'PATCH',
              headers: authHeaders(),
              body: JSON.stringify({ status: statusToSend })
          });

          if (!res.ok) throw new Error("Failed to update status");

          toast.success(`Status updated to ${statusToSend || 'PENDING'}`);
          fetchNominationJobs(); // Refresh
      } catch (error) {
          console.error(error);
          toast.error("Failed to update nomination status.");
      }
  };

  const findLetterByType = (job, type) => {
    if (!job) return null;
    const letters = job.letters || job.job_letters || [];
    const upperType = type.toUpperCase();
    const matched = letters
      .filter(l => (l.letter_type || l.type || '').toUpperCase() === upperType)
      .sort((a, b) => {
        const aTime = new Date(a.created_at || a.date || 0).getTime() || 0;
        const bTime = new Date(b.created_at || b.date || 0).getTime() || 0;
        if (bTime !== aTime) return bTime - aTime;
        return (b.id || 0) - (a.id || 0);
      });
    return matched[0] || null;
  };

  const authLetterFor = job => {
    return findLetterByType(job, 'AUTHORIZATION');
  };

  const findLatestAdditionalLetter = job => {
    if (!job) return null;
    const letters = job.letters || job.job_letters || [];
    const matched = letters
      .filter(l => {
        const type = (l.letter_type || l.type || '').toString().trim().toUpperCase();
        return type && !PRIMARY_LETTER_TYPES.has(type);
      })
      .sort((a, b) => {
        const aTime = new Date(a.created_at || a.date || 0).getTime() || 0;
        const bTime = new Date(b.created_at || b.date || 0).getTime() || 0;
        if (bTime !== aTime) return bTime - aTime;
        return (b.id || 0) - (a.id || 0);
      });
    return matched[0] || null;
  };

  const handleBlClick = async (row) => {
    let job = row;
    try {
      const [detailRes, recordsRes] = await Promise.all([
        fetch(`/api/documentation/jobs/${row.id}/`, { headers: getAuthHeaders(false) }),
        fetch(`${API_BASE}/${row.id}/do-records/`, { headers: getAuthHeaders(false) }),
      ]);
      if (detailRes.ok) {
        const detail = await detailRes.json();
        // The detail serializer doesn't include the computed sl_user_id from the shipping report,
        // so merge the values from the row to keep the dropdown pre-selected.
        job = {
          ...detail,
          sl_user_id: detail.sl_user_id || row.sl_user_id || '',
          sl_user: detail.sl_user || row.sl_user || ''
        };
      }
      if (recordsRes.ok) {
        const records = await recordsRes.json();
        job = { ...job, do_records: Array.isArray(records) ? records : (job.do_records || []) };
      }
    } catch (e) {
      console.error('Failed to fetch full job details', e);
    }

    // Capture the original flat-field DO values at open time.
    // These are frozen here so the virtual "Entry #1" history card always shows
    // the pre-history data, even after the backend updates flat fields on new records.
    const origDoDate = job.do_received_date || null;
    const origDoExpiry = job.do_expiry_date || null;
    const origDropDate = job.drop_off_date || null;
    const origDropExpiry = job.drop_off_expiry_date || null;
    const origDropLocation = job.drop_off_location || null;

    setShippingDetailsDialog(prev => {
      if (prev?.doAttachmentPreviewUrl) URL.revokeObjectURL(prev.doAttachmentPreviewUrl);
      if (prev?.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(prev.dropoffAttachmentPreviewUrl);
      if (prev?.letterAttachmentPreviewUrl) URL.revokeObjectURL(prev.letterAttachmentPreviewUrl);
      if (prev?.newDoEntry?.doAttachmentPreviewUrl) URL.revokeObjectURL(prev.newDoEntry.doAttachmentPreviewUrl);
      if (prev?.newDoEntry?.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(prev.newDoEntry.dropoffAttachmentPreviewUrl);
      return {
      open: true,
      job: job,
      origDoDate,
      origDoExpiry,
      origDropDate,
      origDropExpiry,
      origDropLocation,
      tempDOStatuses: enforceDoReceivedRule([...(job.do_statuses || [])]),
      tempBLType: job.bl_type || job.blType || 'NO OBL',
      tempSLUserId: job.sl_user_id || '',
      tempFreeDays: job.shipping_free_days ?? job.shipping_line_details?.free_days ?? 14,
      tempConfirmFreeDays: !!job.confirm_free_days,
      letterFile: null,
      letterAttachmentPreviewUrl: '',
      doReceivedDate: formatDateForInput(job.do_received_date),
      doExpiryDate: formatDateForInput(job.do_expiry_date),
      dropOffExpiryDate: formatDateForInput(job.drop_off_expiry_date),
      dropOffDate: formatDateForInput(job.drop_off_date),
      dropOffLocation: job.drop_off_location || '',
      doAttachmentFile: null,
      doAttachmentPreviewUrl: '',
      dropOffAttachmentFile: null,
      dropoffAttachmentPreviewUrl: '',
      additionalLetterType: '',
      additionalLetterFile: null,
      newDoEntry: {
        isOpen: false,
        doReceivedDate: '',
        doExpiryDate: '',
        dropOffDate: '',
        dropOffExpiryDate: '',
        dropOffLocation: '',
        doAttachmentFile: null,
        doAttachmentPreviewUrl: '',
        dropOffAttachmentFile: null,
        dropoffAttachmentPreviewUrl: '',
        usePreviousDO: false,
        usePreviousDropoff: false,
        showDropoffExpiry: false,
      },
      saving: false,
    };
  });
};

  const closeShippingDetailsDialog = () => {
    setShippingDetailsDialog(prev => {
      if (prev?.doAttachmentPreviewUrl) URL.revokeObjectURL(prev.doAttachmentPreviewUrl);
      if (prev?.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(prev.dropoffAttachmentPreviewUrl);
      if (prev?.letterAttachmentPreviewUrl) URL.revokeObjectURL(prev.letterAttachmentPreviewUrl);
      if (prev?.newDoEntry?.doAttachmentPreviewUrl) URL.revokeObjectURL(prev.newDoEntry.doAttachmentPreviewUrl);
      if (prev?.newDoEntry?.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(prev.newDoEntry.dropoffAttachmentPreviewUrl);
      return { open: false, job: null };
    });
  };

  const computeEarliestDropoffExpiry = (containers) => {
    const dates = (containers || [])
      .map(c => normalizeDateInput(c.drop_off_expiry_date))
      .filter(Boolean);
    if (!dates.length) return null;
    return [...dates].sort()[0];
  };

  const computeLatestDropoffExpiry = (containers) => {
    const dates = (containers || [])
      .map(c => normalizeDateInput(c.drop_off_expiry_date))
      .filter(Boolean);
    if (!dates.length) return null;
    const sorted = [...dates].sort();
    return sorted[sorted.length - 1];
  };

  const getDropoffExpiryContainerTitle = (containers) => (containers || [])
    .filter(c => c.container_no)
    .map(c => `${c.container_no} - ${c.drop_off_expiry_date ? fmtDateShort(c.drop_off_expiry_date) : 'Enter Date'}`)
    .join('\n');

  const hasMultipleDropoffExpiries = (containers) => new Set(
    (containers || []).filter(c => c.container_no).map(c => c.drop_off_expiry_date).filter(Boolean)
  ).size > 1;

  const openDropoffExpiryModal = (saveMode = 'staged', target = 'dialog') => {
    const job = shippingDetailsDialog.job;
    const containers = (job?.containers || []).filter(c => c.container_no);
    setDropoffExpiryContainers(containers.map(c => ({ ...c, drop_off_expiry_date: normalizeDateInput(c.drop_off_expiry_date || '') })));
    setSelectedDropoffContainerIds([]);
    setBulkDropoffExpiryDate('');
    setDropoffExpirySaveMode(saveMode);
    setDropoffExpiryTarget(target);
    setShowDropoffExpiryModal(true);
  };

  const closeDropoffExpiryModal = () => {
    setShowDropoffExpiryModal(false);
    setDropoffExpiryContainers([]);
    setSelectedDropoffContainerIds([]);
    setBulkDropoffExpiryDate('');
    setDropoffExpirySaveMode('staged');
  };

  const handleDropoffExpirySelectionChange = (id) => {
    setSelectedDropoffContainerIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleDropoffExpiryContainerChange = (id, value) => {
    setDropoffExpiryContainers(prev => prev.map(c => c.id === id ? { ...c, drop_off_expiry_date: value } : c));
  };

  const applyBulkDropoffExpiry = () => {
    if (!bulkDropoffExpiryDate || selectedDropoffContainerIds.length === 0) return;
    setDropoffExpiryContainers(prev => prev.map(c => selectedDropoffContainerIds.includes(c.id) ? { ...c, drop_off_expiry_date: bulkDropoffExpiryDate } : c));
  };

  const saveDropoffExpiryChanges = async () => {
    if (dropoffExpiryContainers.length === 0) return;
    const earliest = computeEarliestDropoffExpiry(dropoffExpiryContainers);

    if (dropoffExpirySaveMode === 'staged') {
      if (dropoffExpiryTarget === 'newdo') {
        setShippingDetailsDialog(d => ({
          ...d,
          job: { ...d.job, containers: dropoffExpiryContainers },
          newDoEntry: { ...d.newDoEntry, dropOffExpiryDate: earliest || '', showDropoffExpiry: true },
        }));
      } else {
        setShippingDetailsDialog(d => ({
          ...d,
          job: { ...d.job, containers: dropoffExpiryContainers },
          dropOffExpiryDate: earliest || '',
        }));
      }
      closeDropoffExpiryModal();
      return;
    }

    setSavingDropoffExpiry(true);
    try {
      const job = shippingDetailsDialog.job;
      const containerRes = await fetch(`${API_BASE}/bulk-update-containers/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(dropoffExpiryContainers.map(c => ({ id: c.id, drop_off_expiry_date: c.drop_off_expiry_date || null })))
      });
      if (!containerRes.ok) throw new Error('Failed to update container drop-off expiry dates.');

      const jobRes = await fetch(`${API_BASE}/${job.id}/`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ drop_off_expiry_date: earliest })
      });
      if (!jobRes.ok) throw new Error('Failed to update the drop-off expiry date.');
      const updatedJob = await jobRes.json();
      setShippingDetailsDialog(d => ({
        ...d,
        job: { ...updatedJob, containers: dropoffExpiryContainers },
        dropOffExpiryDate: earliest || '',
      }));
      toast.success('Container drop-off expiry dates saved.');
      closeDropoffExpiryModal();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to save drop-off expiry dates.');
    } finally {
      setSavingDropoffExpiry(false);
    }
  };

  // Fetch + preview the BL document on demand (never loaded with the page)
  const viewBlDocument = async () => {
    const job = shippingDetailsDialog.job;
    if (!job || blLoading) return;
    setBlLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${job.id}/bl-document/`, { headers: authHeaders(false) });
      if (!res.ok) throw new Error('not found');
      const data = await res.json();
      if (!data.url) throw new Error('not found');
      const fileRes = await fetch(data.url, { headers: authHeaders(false) });
      if (!fileRes.ok) throw new Error('not found');
      const blob = await fileRes.blob();
      const blobUrl = URL.createObjectURL(blob);
      const fileName = data.name || 'BL Document';
      const opened = openFilePreview(blobUrl, /\.[a-z0-9]+$/i.test(fileName) ? fileName : `${fileName}.pdf`);
      if (!opened) {
        URL.revokeObjectURL(blobUrl);
        throw new Error('not found');
      }
    } catch (e) {
      toast.error('The BL document is not available for this job.');
    } finally {
      setBlLoading(false);
    }
  };

  const handleConfirmFreeDaysChange = (checked) => {
    if (checked) {
      setShowFreeDaysConfirm(true);
    } else {
      setShippingDetailsDialog(d => ({ ...d, tempConfirmFreeDays: false }));
    }
  };

  const saveShippingDetails = async () => {
    const { 
      job, tempBLType, tempDOStatuses, tempSLUserId, letterFile,
      doReceivedDate, doExpiryDate, dropOffExpiryDate, dropOffDate, dropOffLocation,
      doAttachmentFile, dropOffAttachmentFile, additionalLetterType, additionalLetterFile,
      newDoEntry, tempFreeDays, tempConfirmFreeDays
    } = shippingDetailsDialog;
    if (!job) return;
    const normalizedAdditionalLetterType = (additionalLetterType || '').trim();

    const isLclCargo = ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('LCL')) ||
      ((job?.cargo_type || job?.shipment_type || '').toString().toUpperCase().includes('VEHICLE'));
    const hasDoReceivedStatus = tempDOStatuses.includes(DO_RECEIVED);
    const hasAwaitingDropoffStatus = tempDOStatuses.includes(DO_RECEIVED_AWAITING_DROPOFF);
    const shouldShowDropoffFields = hasDoReceivedStatus && !hasAwaitingDropoffStatus && !isLclCargo;

    const containers = job?.containers || [];
    const singleContainer = containers.length === 1 ? containers[0] : null;
    const isMultiContainer = containers.length > 1;
    const containerDropOffExpiry = computeEarliestDropoffExpiry(containers) || '';
    const effectiveDropOffExpiry = isMultiContainer
      ? (containerDropOffExpiry || dropOffExpiryDate || '')
      : dropOffExpiryDate;
    const maxDropOffExpiry = isMultiContainer
      ? (computeLatestDropoffExpiry(containers) || dropOffExpiryDate || '')
      : dropOffExpiryDate;
    const effectiveNewDropOffExpiry = newDoEntry.dropOffExpiryDate || '';

    // Determine if this is a first-time DO entry (no history) or adding a new one
    // Use origDoDate (frozen at dialog open) so this stays stable after flat field updates
    const existingDoRecords = Array.isArray(job.do_records) ? job.do_records : [];
    const { origDoDate, origDoExpiry, origDropDate, origDropExpiry, origDropLocation } = shippingDetailsDialog;
    const hasExistingDoRecord = existingDoRecords.length > 0;

    // For new DO entry form fields
    const newDo = newDoEntry || {};
    // Only add a new DO entry if the user opened the form or filled its fields
    const isAddingNewDoEntry = newDo.isOpen || !!(newDo.doReceivedDate || newDo.doAttachmentFile || newDo.usePreviousDO);

    const today = new Date().toISOString().split('T')[0];

    // Validate DO dates — use newDoEntry fields when the new entry form is active, legacy fields otherwise
    if (hasDoReceivedStatus || hasAwaitingDropoffStatus) {
      if (isAddingNewDoEntry) {
        // Validate the new DO entry form only when the user is adding one
        if (!newDo.usePreviousDO) {
          if (!newDo.doReceivedDate) {
            toast.error('Please provide DO Received Date for the new entry.');
            return;
          }
          if (newDo.doReceivedDate > today) {
            toast.error('DO Received Date cannot be a future date.');
            return;
          }
          if (newDo.doExpiryDate && newDo.doExpiryDate < newDo.doReceivedDate) {
            toast.error('DO Expiry Date cannot be before DO Received Date.');
            return;
          }
        }
        if (shouldShowDropoffFields && !newDo.usePreviousDropoff) {
          if (!newDo.dropOffDate) {
            toast.error('Please provide Drop-Off Received Date for the new entry.');
            return;
          }
          const newDoDropOffDate = normalizeDateInput(newDo.dropOffDate);
          if (newDoDropOffDate > today) {
            toast.error('Drop-Off Received Date cannot be a future date.');
            return;
          }
          const hasNewDropOffExpiry = isMultiContainer
            ? (containers.length > 0 && containers.every(c => normalizeDateInput(c.drop_off_expiry_date)))
            : !!normalizeDateInput(newDo.dropOffExpiryDate);
          if (!hasNewDropOffExpiry) {
            toast.error(isMultiContainer ? 'Drop-Off Expiry Date is required for all containers.' : 'Please provide Drop-Off Expiry Date for the new entry.');
            return;
          }
          const maxNewDropOffExpiry = isMultiContainer
            ? (computeLatestDropoffExpiry(containers) || '')
            : (normalizeDateInput(newDo.dropOffExpiryDate) || '');
          if (maxNewDropOffExpiry && maxNewDropOffExpiry < newDoDropOffDate) {
            toast.error('Drop-Off Expiry Date cannot be before Drop-Off Received Date.');
            return;
          }
          if (newDo.dropOffDate && !newDo.dropOffAttachmentFile) {
            toast.error('Please attach Drop-Off Attachment for the new entry.');
            return;
          }
        }
      } else if (!hasExistingDoRecord) {
        // First-time save — validate legacy fields
        if (!doReceivedDate) {
          toast.error('Please provide DO Received Date.');
          return;
        }
        if (doReceivedDate > today) {
          toast.error('DO Received Date cannot be a future date.');
          return;
        }
        if (doExpiryDate && doExpiryDate < doReceivedDate) {
          toast.error('DO Expiry Date cannot be before DO Received Date.');
          return;
        }
        if (shouldShowDropoffFields) {
          if (!dropOffDate) {
            toast.error('Please provide Drop-Off Received Date.');
            return;
          }
          if (dropOffDate > today) {
            toast.error('Drop-Off Received Date cannot be a future date.');
            return;
          }
          if (maxDropOffExpiry && maxDropOffExpiry < normalizeDateInput(dropOffDate)) {
            toast.error('Drop-Off Expiry Date cannot be before Drop-Off Received Date.');
            return;
          }
          // Drop-Off Attachment is required only when both Drop-Off Received and Expiry dates are entered.
          if (dropOffDate && effectiveDropOffExpiry && !dropOffAttachmentFile) {
            toast.error(`Please attach Drop-Off Attachment. (dropOffDate: ${dropOffDate || 'empty'}, expiry: ${effectiveDropOffExpiry || 'empty'})`);
            return;
          }
        }
      }
    }

    // Permission check
    if (!hasPermission('SHIPPING REPORTS', 'can_edit')) {
        toast.error("You do not have permission to save changes.");
        return;
    }

    if (!tempConfirmFreeDays) {
        toast.error("Please select 'Confirm Free Days' before saving.");
        return;
    }

    setShippingDetailsDialog(d => ({ ...d, saving: true }));

    try {
      const promises = [];
      const patchData = {};

      if (tempBLType && tempBLType !== (job.bl_type || job.blType)) {
        patchData.bl_type = tempBLType;
      }
      const cleanedDOStatuses = enforceDoReceivedRule(tempDOStatuses);
      if (JSON.stringify(cleanedDOStatuses) !== JSON.stringify(job.do_statuses)) {
        patchData.do_statuses = cleanedDOStatuses;
      }
      if (tempSLUserId) {
        const staff = shippingStaff.find(s => String(s.id) === String(tempSLUserId));
        const newName = staff ? staff.name : '';
        if (newName && newName !== job.sl_user) {
          patchData.sl_user = newName;
        }
      }
      const parsedFreeDays = tempFreeDays === '' || tempFreeDays === null ? null : parseInt(tempFreeDays, 10);
      if (parsedFreeDays !== (job.shipping_free_days ?? null)) {
        patchData.shipping_free_days = parsedFreeDays;
      }
      if (!!tempConfirmFreeDays !== !!job.confirm_free_days) {
        patchData.confirm_free_days = !!tempConfirmFreeDays;
      }

      // First-time DO: save dates directly into a JobDORecord so every DO lives in DO history
      if (!hasExistingDoRecord && !isAddingNewDoEntry && (doReceivedDate || doExpiryDate || (shouldShowDropoffFields && (dropOffDate || effectiveDropOffExpiry || dropOffLocation)))) {
        const doFd = new FormData();
        doFd.append('skip_auto_migrate', 'true');
        if (doReceivedDate) doFd.append('do_received_date', doReceivedDate);
        if (doExpiryDate) doFd.append('do_expiry_date', doExpiryDate);
        if (doAttachmentFile) doFd.append('do_attachment', doAttachmentFile);
        if (shouldShowDropoffFields) {
          if (dropOffDate) doFd.append('drop_off_date', dropOffDate);
          if (effectiveDropOffExpiry) doFd.append('drop_off_expiry_date', effectiveDropOffExpiry);
          if (dropOffLocation) doFd.append('drop_off_location', dropOffLocation);
          if (dropOffAttachmentFile) doFd.append('drop_off_attachment', dropOffAttachmentFile);
        }
        promises.push(
          fetch(`${API_BASE}/${job.id}/do-records/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: doFd,
          })
        );
      }

      if (Object.keys(patchData).length > 0) {
        promises.push(
          fetch(`${API_BASE}/${job.id}/`, {
            method: 'PATCH',
            headers: authHeaders(),
            body: JSON.stringify(patchData),
          })
        );
      }

      if (letterFile) {
        const fd = new FormData();
        fd.append('letter_type', 'AUTHORIZATION');
        fd.append('file', letterFile);
        promises.push(
          fetch(`${API_BASE}/${job.id}/letters/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: fd,
          })
        );
      }

      // New DO record entry (subsequent entries via history)
      if (isAddingNewDoEntry) {
        const doFd = new FormData();
        if (newDo.usePreviousDO) {
          doFd.append('use_previous_do', 'true');
        } else {
          doFd.append('do_received_date', newDo.doReceivedDate);
          if (newDo.doExpiryDate) doFd.append('do_expiry_date', newDo.doExpiryDate);
          if (newDo.doAttachmentFile) doFd.append('do_attachment', newDo.doAttachmentFile);
        }
        if (shouldShowDropoffFields) {
          if (newDo.usePreviousDropoff) {
            doFd.append('use_previous_dropoff', 'true');
          } else {
            if (newDo.dropOffDate) doFd.append('drop_off_date', newDo.dropOffDate);
            if (effectiveNewDropOffExpiry) doFd.append('drop_off_expiry_date', effectiveNewDropOffExpiry);
            if (newDo.dropOffLocation) doFd.append('drop_off_location', newDo.dropOffLocation);
            if (newDo.dropOffAttachmentFile) doFd.append('drop_off_attachment', newDo.dropOffAttachmentFile);
          }
        }
        promises.push(
          fetch(`${API_BASE}/${job.id}/do-records/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: doFd,
          })
        );
      }

      if (shouldShowDropoffFields && containers.length > 0) {
        const effectiveDate = isAddingNewDoEntry ? effectiveNewDropOffExpiry : (hasExistingDoRecord ? '' : effectiveDropOffExpiry);
        const containerUpdates = isMultiContainer
          ? containers.map(c => ({ id: c.id, drop_off_expiry_date: c.drop_off_expiry_date || null }))
          : effectiveDate ? [{ id: singleContainer.id, drop_off_expiry_date: effectiveDate }] : [];
        if (containerUpdates.length > 0) {
          promises.push(
            fetch(`${API_BASE}/bulk-update-containers/`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify(containerUpdates)
            })
          );
        }
      }

      if (normalizedAdditionalLetterType && additionalLetterFile) {
        const fd = new FormData();
        fd.append('letter_type', normalizedAdditionalLetterType);
        fd.append('file', additionalLetterFile);
        promises.push(
          fetch(`${API_BASE}/${job.id}/letters/`, {
            method: 'POST',
            headers: authHeaders(false),
            body: fd,
          })
        );
      }

      if (promises.length === 0) {
        closeShippingDetailsDialog();
        return;
      }

      const responses = await Promise.all(promises);
      const allOk = responses.every(res => res.ok);

      if (allOk) {
        toast.success('Shipping details saved successfully!');
        closeShippingDetailsDialog();
        fetchJobs();
        fetchPendingJobs();
      } else {
        for (const res of responses) {
          if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to save details: ${errorText}`);
          }
        }
      }
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'An error occurred while saving.');
    } finally {
      setShippingDetailsDialog(d => ({ ...d, saving: false }));
    }
  };

  const startEditDoRecord = (rec) => {
    setEditingDoRecord(rec.id);
    setDoRecordEditDraft({
      do_received_date: formatDateForInput(rec.do_received_date),
      do_expiry_date: formatDateForInput(rec.do_expiry_date),
      drop_off_date: formatDateForInput(rec.drop_off_date),
      drop_off_expiry_date: formatDateForInput(rec.drop_off_expiry_date),
      drop_off_location: rec.drop_off_location || '',
      doAttachmentFile: null,
      dropOffAttachmentFile: null,
    });
  };

  const cancelEditDoRecord = () => {
    setEditingDoRecord(null);
    setDoRecordEditDraft({});
  };

  const updateDoRecordDraft = (field, value) => {
    setDoRecordEditDraft(d => ({ ...d, [field]: value }));
  };

  const updateDoRecordInState = (updated) => {
    setShippingDetailsDialog(d => ({
      ...d,
      job: {
        ...d.job,
        do_records: (d.job?.do_records || []).map(r => r.id === updated.id ? updated : r)
      }
    }));
  };

  const updateOriginalDoInState = (updated) => {
    const newLetters = [];
    if (updated.do_attachment_url) {
      newLetters.push({ letter_type: 'DO ATTACHMENT', generated_file: updated.do_attachment_url, created_at: new Date().toISOString() });
    }
    if (updated.drop_off_attachment_url) {
      newLetters.push({ letter_type: 'DROPOFF ATTACHMENT', generated_file: updated.drop_off_attachment_url, created_at: new Date().toISOString() });
    }
    setShippingDetailsDialog(d => ({
      ...d,
      origDoDate: updated.do_received_date ? formatDateForInput(updated.do_received_date) : d.origDoDate,
      origDoExpiry: updated.do_expiry_date ? formatDateForInput(updated.do_expiry_date) : d.origDoExpiry,
      origDropDate: updated.drop_off_date ? formatDateForInput(updated.drop_off_date) : d.origDropDate,
      origDropExpiry: updated.drop_off_expiry_date ? formatDateForInput(updated.drop_off_expiry_date) : d.origDropExpiry,
      origDropLocation: updated.drop_off_location !== undefined ? (updated.drop_off_location || '') : d.origDropLocation,
      job: {
        ...d.job,
        do_received_date: updated.do_received_date,
        do_expiry_date: updated.do_expiry_date,
        drop_off_date: updated.drop_off_date,
        drop_off_expiry_date: updated.drop_off_expiry_date,
        drop_off_location: updated.drop_off_location,
        letters: [...(d.job?.letters || []), ...newLetters],
      }
    }));
  };

  const removeDoRecordFromState = (recordId) => {
    setShippingDetailsDialog(d => ({
      ...d,
      job: {
        ...d.job,
        do_records: (d.job?.do_records || []).filter(r => r.id !== recordId)
      }
    }));
  };

  const saveDoRecordEdit = async (rec) => {
    const draft = doRecordEditDraft;
    if (!draft) return;
    setDoRecordSaving(true);
    try {
      const job = shippingDetailsDialog.job;
      const containers = (job?.containers || []).filter(c => c.container_no);
      const isMultiContainer = containers.length > 1;
      const effectiveDropOffExpiry = isMultiContainer
        ? (computeEarliestDropoffExpiry(containers) || '')
        : (normalizeDateInput(draft.drop_off_expiry_date) || '');
      const maxDropOffExpiry = isMultiContainer
        ? (computeLatestDropoffExpiry(containers) || '')
        : (normalizeDateInput(draft.drop_off_expiry_date) || '');

      if (draft.do_expiry_date && draft.do_received_date && draft.do_expiry_date < draft.do_received_date) {
        toast.error('DO Expiry Date cannot be before DO Received Date.');
        return;
      }
      if (showDropoffFields) {
        const dropOffReceived = normalizeDateInput(draft.drop_off_date);
        if (draft.do_received_date && !dropOffReceived) {
          toast.error('Drop-Off Received Date is required.');
          return;
        }
        if (isMultiContainer && draft.do_received_date) {
          const missingExpiries = containers.filter(c => !normalizeDateInput(c.drop_off_expiry_date));
          if (missingExpiries.length > 0) {
            toast.error(`Drop-Off Expiry Date is required for container(s): ${missingExpiries.map(c => c.container_no).join(', ')}.`);
            return;
          }
        }
        if (draft.do_received_date && !effectiveDropOffExpiry) {
          toast.error('Drop-Off Expiry Date is required.');
          return;
        }
        if (dropOffReceived && maxDropOffExpiry && maxDropOffExpiry < dropOffReceived) {
          toast.error('Drop-Off Expiry Date cannot be before Drop-Off Received Date.');
          return;
        }
        if (draft.do_received_date && !draft.dropOffAttachmentFile && !rec.drop_off_attachment_url) {
          toast.error('Drop-Off Attachment is required.');
          return;
        }
      }

      const isOriginal = rec.id === 'original' || rec._virtual;
      const fd = new FormData();
      if (isOriginal) {
        fd.append('job_id', job.id);
      } else {
        fd.append('record_id', rec.id);
      }
      if (!isMultiContainer && containers[0]?.id) {
        fd.append('container_id', containers[0].id);
      }
      fd.append('do_received_date', draft.do_received_date || '');
      fd.append('do_expiry_date', draft.do_expiry_date || '');
      if (showDropoffFields) {
        fd.append('drop_off_date', normalizeDateInput(draft.drop_off_date) || '');
        fd.append('drop_off_expiry_date', effectiveDropOffExpiry);
        fd.append('drop_off_location', draft.drop_off_location || '');
      }
      if (draft.doAttachmentFile) fd.append('do_attachment', draft.doAttachmentFile);
      if (draft.dropOffAttachmentFile) fd.append('drop_off_attachment', draft.dropOffAttachmentFile);

      const endpoint = isOriginal ? 'update-original-do-record' : 'update-do-record';
      const res = await fetch(`${API_BASE}/${endpoint}/`, {
        method: 'PATCH',
        headers: authHeaders(false),
        body: fd,
      });
      if (!res.ok) throw new Error('Failed to update DO record.');
      const data = await res.json();
      if (isOriginal) {
        updateOriginalDoInState(data);
      } else {
        updateDoRecordInState(data);
      }
      toast.success('DO record updated.');
      cancelEditDoRecord();
      fetchJobs();
      fetchPendingJobs();
    } catch (e) {
      toast.error(e.message || 'Failed to update DO record.');
    } finally {
      setDoRecordSaving(false);
    }
  };

  const openDeleteDoRecordModal = (rec) => setDeletingDoRecord(rec.id);
  const closeDeleteDoRecordModal = () => setDeletingDoRecord(null);

  const doDeleteDoRecord = async () => {
    if (!deletingDoRecord) return;
    setDoRecordSaving(true);
    try {
      const res = await fetch(`${API_BASE}/delete-do-record/`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({ record_id: deletingDoRecord }),
      });
      if (!res.ok) throw new Error('Failed to delete DO record.');
      removeDoRecordFromState(deletingDoRecord);
      toast.success('DO record deleted.');
      fetchJobs();
      fetchPendingJobs();
    } catch (e) {
      toast.error(e.message || 'Failed to delete DO record.');
    } finally {
      setDoRecordSaving(false);
      closeDeleteDoRecordModal();
    }
  };

  const filteredJobs = useMemo(() => {
    const term = appliedSearch.term.trim().toUpperCase();
    return jobs.filter(j => {
      if (term) {
        if (appliedSearch.by === 'IDF' && !(j.idf_no || j.idfNo || '').toUpperCase().includes(term)) return false;
        if (appliedSearch.by === 'BL' && !(getBlValue(j) || '').toUpperCase().includes(term)) return false;
        if (appliedSearch.by === 'Customer' && !(j.customer_name || '').toUpperCase().includes(term)) return false;
      }
      if (slUserFilter === '__NO_USER__') return !(j.sl_user || '').trim();
      if (slUserFilter) return (j.sl_user || '') === slUserFilter;
      return true;
    });
  }, [jobs, appliedSearch, slUserFilter]);

  const filteredPendingJobs = useMemo(() => {
    const term = appliedSearch.term.trim().toUpperCase();
    return pendingJobs.filter(j => {
      if (term) {
        if (appliedSearch.by === 'IDF' && !(j.idf_no || j.idfNo || '').toUpperCase().includes(term)) return false;
        if (appliedSearch.by === 'BL' && !(getBlValue(j) || '').toUpperCase().includes(term)) return false;
        if (appliedSearch.by === 'Customer' && !(j.customer_name || '').toUpperCase().includes(term)) return false;
      }
      if (slUserFilter === '__NO_USER__') return !(j.sl_user || '').trim();
      if (slUserFilter) return (j.sl_user || '') === slUserFilter;
      return true;
    });
  }, [pendingJobs, appliedSearch, slUserFilter]);

  const shippingToDoJobs = useMemo(() => {
    return filteredJobs.filter(j => !j.do_statuses.includes(DO_RECEIVED) && !j.do_statuses.includes(DO_RECEIVED_AWAITING_DROPOFF));
  }, [filteredJobs]);

  const combinedShippingJobs = useMemo(() => {
    const mergedJobs = [...filteredJobs, ...filteredPendingJobs];
    return Array.from(new Map(mergedJobs.map(job => [job.id, job])).values());
  }, [filteredJobs, filteredPendingJobs]);

  const doReceivedJobs = useMemo(() => {
    const base = combinedShippingJobs.filter(j => j.do_statuses.includes(DO_RECEIVED) && !j.do_statuses.includes(DO_RECEIVED_AWAITING_DROPOFF));
    if (appliedSearch.term.trim()) return base; // searching looks across all records
    // Default: hide released jobs and only DO records received within the last 2 months
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 2);
    cutoff.setHours(0, 0, 0, 0);
    return base.filter(j => {
      if (isReleasedJob(j)) return false;
      if (!j.do_received_date) return true;
      const d = new Date(j.do_received_date);
      return isNaN(d.getTime()) || d >= cutoff;
    });
  }, [combinedShippingJobs, appliedSearch]);

  const doReceivedAwaitingDropoffJobs = useMemo(() => {
    return combinedShippingJobs.filter(j => j.do_statuses.includes(DO_RECEIVED_AWAITING_DROPOFF));
  }, [combinedShippingJobs]);

  const nominationStatusCounts = useMemo(() => {
    const counts = { ALL: nominationJobs.length };
    nominationJobs.forEach(j => {
      const status = j.nomination_status || '';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [nominationJobs]);

  const filteredNominationJobs = useMemo(() => {
    const term = appliedSearch.term.trim().toUpperCase();
    return nominationJobs.filter(j => {
      if (!nominationStatusFilter.includes(j.nomination_status || '')) return false;
      if (!term) return true;
      if (appliedSearch.by === 'IDF') {
        return (j.idf_no || j.idfNo || '').toUpperCase().includes(term);
      }
      if (appliedSearch.by === 'BL') {
        return (getBlValue(j) || '').toUpperCase().includes(term);
      }
      if (appliedSearch.by === 'Customer') {
        return (j.customer_name || '').toUpperCase().includes(term);
      }
      return true;
    });
  }, [nominationJobs, nominationStatusFilter, appliedSearch]);

  const shippingUserOptions = useMemo(() => {
    const names = [...new Set((shippingStaff || []).map(s => (s.name || '').trim()).filter(Boolean))];
    return names.map(name => ({ id: name, text: name }));
  }, [shippingStaff]);

  const handleNominationCardClick = useCallback((id) => {
    if (id === 'ALL') {
      setNominationStatusFilter(NOMINATION_STATUS_OPTIONS.map(o => o.id));
    } else {
      setNominationStatusFilter([id]);
    }
  }, []);

  const handleExport = (format) => {
    const isExcel = format === 'excel';
    if (activeTab === 'shipping') {
      isExcel ? exportExcelSection(shippingToDoJobs, 'ShippingReport_ToDo') : exportPDFSection(shippingToDoJobs, 'ShippingReport_ToDo');
    } else if (activeTab === 'pending_reports') {
      isExcel ? exportExcelSection(filteredPendingJobs, 'Pending_Report') : exportPDFSection(filteredPendingJobs, 'Pending_Report');
    } else if (activeTab === 'consignee_nomination') {
      isExcel ? exportExcelSection(filteredNominationJobs, 'Consignee_Nomination_Report') : exportPDFSection(filteredNominationJobs, 'Consignee_Nomination_Report');
    } else if (activeTab === 'do_received_awaiting_dropoff') {
      isExcel ? exportExcelSection(doReceivedAwaitingDropoffJobs, 'DO_Received_Awaiting_Dropoff_Report') : exportPDFSection(doReceivedAwaitingDropoffJobs, 'DO_Received_Awaiting_Dropoff_Report');
    } else {
      isExcel ? exportExcelSection(doReceivedJobs, 'DO_Received_Report') : exportPDFSection(doReceivedJobs, 'DO_Received_Report');
    }
  };

  const resetSwapDialog = () => {
    setSwapFromUser('');
    setSwapToUser('');
    setSwappingUsers(false);
    setShowSwapDialog(false);
  };

  const handleSwapUsers = async () => {
    if (!canEditShippingReport) {
      toast.error('Permission denied.');
      return;
    }

    if (!swapFromUser || !swapToUser) {
      toast.warn('Please select both From User and To User.');
      return;
    }

    if (swapFromUser === swapToUser) {
      toast.warn('From User and To User must be different.');
      return;
    }

    const visibleJobIds = [
      ...new Set([
        ...shippingToDoJobs.map(j => j.id),
        ...filteredPendingJobs.map(j => j.id),
        ...doReceivedJobs.map(j => j.id),
        ...doReceivedAwaitingDropoffJobs.map(j => j.id),
      ].filter(Boolean))
    ];
    if (visibleJobIds.length === 0) {
      toast.warn('No visible records found across Shipping Report tabs to swap.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to swap the SL Users?');
    if (!confirmed) {
      return;
    }

    setSwappingUsers(true);
    try {
      const res = await fetch(`${API_BASE}/swap-shipping-users/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          from_user: swapFromUser,
          to_user: swapToUser,
          job_ids: visibleJobIds,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Failed to swap users.');
      }

      const data = await res.json();
      toast.success(
        `Swap completed. Updated: ${data.updated_count || 0} | ${swapFromUser} -> ${swapToUser}: ${data.from_to_count || 0} | ${swapToUser} -> ${swapFromUser}: ${data.to_from_count || 0} | Unchanged: ${data.unchanged_count || 0}`
      );
      resetSwapDialog();
      fetchJobs();
      fetchPendingJobs();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Failed to swap users.');
    } finally {
      setSwappingUsers(false);
    }
  };

  const renderContainerSize = (job) => {
    const isLcl = (job.shipment_type || '').toString().toUpperCase() === 'LCL' ||
                (job.cargo_type || '').toString().toUpperCase() === 'LCL';
    if (isLcl) {
      return 'LCL';
    }
    const summaryString = job.container_size_summary;
    if (!summaryString || typeof summaryString !== 'string') {
      return '-';
    }
    const parts = summaryString.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length === 0) {
      return '-';
    }
    return (
        <ul style={{ margin: 0, paddingLeft: 16, whiteSpace: 'nowrap' }}>
            {parts.map((item, i) => (
                <li key={i}>{item}</li>
            ))}
        </ul>
    );
  };

  function renderDangerousClass(job) {
    const dc = job.dangerous_class;
    if (!dc) return '-';
    const arr = Array.isArray(dc) ? dc : (typeof dc === 'string' ? dc.split(',').map(s => s.trim()).filter(Boolean) : []);
    if (arr.length === 0) return '-';
    // display only class part before ' - ' and as ul
    return (<ul style={{ margin: 0, paddingLeft: 16 }}>
      {arr.map((item, i) => {
        const first = item.split(' - ')[0].trim();
        return <li key={i}>{toUpper(first)}</li>;
      })}
    </ul>);
  }

  const renderIcdNominationCell = j =>
    j.icd_nomination || j.icd_nomination_status || '-' || '-';

  const renderAttachmentCell = (job, type, label) => {
    let url = null;
    if (type === 'DO ATTACHMENT') {
      url = job.do_attachment_url || null;
    } else if (type === 'DROPOFF ATTACHMENT') {
      url = job.drop_off_attachment_url || null;
    } else if (type === 'AUTHORIZATION') {
      url = job.authorization_letter_url || null;
      if (!url) {
        const letter = findLetterByType(job, type);
        url = letter ? (letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || null) : null;
      }
    }
    if (url) {
      return (
        <button
          type="button"
          className="add-btn"
          style={{ background: '#007bff', padding: '2px 6px' }}
          onClick={() => openFilePreview(url, label)}
        >
          View
        </button>
      );
    }
    return <span style={{ color: '#c77f00', fontSize: 12 }}>PENDING</span>;
  };

  // 4. Add a top-level permission check for viewing the report
  if (!hasPermission('SHIPPING REPORTS', 'can_view')) {
    return (
        <div className="job-list-container" style={{ overflowX: 'auto' }}>
            <h2>Permission Denied</h2>
            <p>You do not have permission to view this report.</p>
        </div>
    );
  }

  return (
    <div className="job-list-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Shipping Reports</h2>
        <button onClick={() => { fetchJobs(); fetchPendingJobs(); fetchNominationJobs(); }} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>
      <div className="toolbar" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, alignItems: 'flex-start' }}>
        {/* Row 1: search inputs + user filter */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'flex-start' }}>
          <Select2Dropdown
            options={[
              { id: 'IDF', text: 'IDF' },
              { id: 'BL', text: 'BL' },
              { id: 'Customer', text: 'Customer' }
            ]}
            value={searchBy}
            onChange={setSearchBy}
            valueKey="id"
            displayKey="text"
            placeholder="Search by"
          />
          <input
            type="text"
            placeholder="Enter search..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setAppliedSearch({ by: searchBy, term: (searchInput || '').trim().toUpperCase() }); }}
            className="search-input"
          />
          <button
            onClick={() => setAppliedSearch({ by: searchBy, term: (searchInput || '').trim().toUpperCase() })}
            className="add-btn"
            style={{ backgroundColor: '#28a745' }}
          >
            Search
          </button>
          <button
            onClick={() => { setSearchInput(''); setAppliedSearch({ by: searchBy, term: '' }); }}
            className="add-btn"
            style={{ backgroundColor: '#6c757d' }}
          >
            Clear
          </button>
          <div style={{
            width: (() => {
              const names = ['All SL Users', 'NO USER', ...shippingStaff.map(s => s.name)];
              const longest = names.reduce((a, b) => a.length > b.length ? a : b, '');
              return `${Math.max(longest.length * 8.5 + 48, 160)}px`;
            })()
          }}>
            <Select2Dropdown
              options={[
                { id: '', text: 'All SL Users' },
                { id: '__NO_USER__', text: 'NO USER' },
                ...shippingStaff.map(s => ({ id: s.name, text: s.name }))
              ]}
              value={slUserFilter}
              onChange={setSlUserFilter}
              valueKey="id"
              displayKey="text"
              placeholder="Filter by SL User"
            />
          </div>
        </div>
        {/* Row 2: buttons centered */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center' }}>
          <button
            onClick={() => handleExport('excel')}
            className="add-btn"
            style={{ backgroundColor: '#007bff' }}
          >
            Download Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="add-btn"
            style={{ backgroundColor: '#17a2b8' }}
          >
            Download PDF
          </button>
        </div>
      </div>

      {canEditShippingReport && activeTab !== 'consignee_nomination' && (
        <div style={{ marginBottom: 16 }}>
          <button
            type="button"
            className="add-btn"
            style={{ backgroundColor: '#6f42c1' }}
            onClick={() => setShowSwapDialog(true)}
          >
            <i className="fa fa-exchange" style={{ marginRight: 6 }} />
            Swap SL Users
          </button>
        </div>
      )}

      <div className="custom-tabs-container" style={{ marginTop: 16 }}>
        <button
          className={`custom-tab ${activeTab === 'pending_reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending_reports')}
        >
          Pending Reports ({filteredPendingJobs.length})
        </button>
        <button
          className={`custom-tab ${activeTab === 'shipping' ? 'active' : ''}`}
          onClick={() => setActiveTab('shipping')}
        >
          Current Report ({shippingToDoJobs.length})
        </button>
        <button
          className={`custom-tab ${activeTab === 'do_received' ? 'active' : ''}`}
          onClick={() => setActiveTab('do_received')}
        >
          DO Received Report ({doReceivedJobs.length})
        </button>
        <button
          className={`custom-tab ${activeTab === 'do_received_awaiting_dropoff' ? 'active' : ''}`}
          onClick={() => setActiveTab('do_received_awaiting_dropoff')}
        >
          DO Received, Awaiting Dropoff ({doReceivedAwaitingDropoffJobs.length})
        </button>
        <button
          className={`custom-tab ${activeTab === 'consignee_nomination' ? 'active' : ''} ${(nominationStatusCounts[''] || 0) > 0 ? 'stage-3-flashing' : ''}`}
          onClick={() => setActiveTab('consignee_nomination')}
        >
          Consignee Nomination ({filteredNominationJobs.length})
        </button>
        <button
          className={`custom-tab ${activeTab === 'deposits' ? 'active' : ''} ${deposits.filter(d => (d.deposit_status || 'PENDING') === 'PENDING').length > 0 ? 'deposit-flashing' : ''}`}
          onClick={() => setActiveTab('deposits')}
        >
          Deposits ({deposits.filter(d => (d.deposit_status || 'PENDING') === 'PENDING').length})
        </button>
      </div>

      {activeTab === 'shipping' && (
        <>
          <div style={{ fontWeight: 'bold', background: '#eef5ff', padding: 8, borderRadius: 6, marginBottom: 8, marginTop: 16 }}>
            SHIPPING REPORT
          </div>
          <TableScrollContainer>
            <table className="jobs-table" style={{ overflow: 'visible' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>IDF NO</th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>VESSEL</span><br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETA</span><br /><span style={{ fontWeight: 'bold' }}>ATA</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETB</span><br /><span style={{ fontWeight: 'bold' }}>ATB</span></th>
                  <th>SHIPPING LINE</th>
                  <th style={{ fontWeight: 'bold' }}>CUSTOMER NAME</th>
                  <th>CLIENT REF</th>
                  <th>BL</th>
                  <th>CONTAINER SIZE</th>
                  <th>ICD</th>
                  <th>DANGEROUS CLASS</th>
                  <th>ICD NOMINATION</th>
                  <th>LETTER</th>
                  <th>BL TYPE</th>
                  <th>DO STATUS</th>
                  <th>SL USER</th>
                  <th>MANIFEST OUT</th>
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={18} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
                {!loading && shippingToDoJobs.length === 0 && (<tr><td colSpan={18} className="no-data">No records found.</td></tr>)}
                {!loading && shippingToDoJobs.map(j => (
                  <tr 
                    key={j.id} 
                    className={j.manifest_ready_date && !j.sl_acknowledged ? 'flash-orange' : ''}
                    onClick={() => {
                        // 5. Conditionally allow acknowledging
                        if (hasPermission('SHIPPING REPORTS', 'can_edit')) {
                            handleAcknowledgeSl(j);
                        }
                    }}
                    title={j.manifest_ready_date && !j.sl_acknowledged ? 'Manifest is Out. Click to acknowledge.' : ''}
                    style={{ cursor: j.manifest_ready_date && !j.sl_acknowledged && hasPermission('SHIPPING REPORTS', 'can_edit') ? 'pointer' : 'default' }}
                  >
                    <td><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openJobFormModal(j); }}>{j.id}</a></td>
                    <td>{j.idf_no || '-'}</td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>{toUpper(j.vessel)}</span><br /><span style={{ fontWeight: 'normal' }}>{toUpper(j.voyage_number)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.eta)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.ata)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.etb)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.atb)}</span></td>
                    <td>{toUpper(j.shipping_line_name || j.shipping_line || '') || '-'}</td>
                    <td style={{ fontWeight: 'bold' }}>{toUpper(j.customer_name)}</td>
                    <td>{j.client_ref || '-'}</td>
                    {/* 6. Conditionally render the BL link as interactive or plain text */}
                    <td>
                        {renderBlNumber(j, hasPermission('SHIPPING REPORTS', 'can_edit') ? handleBlClick : undefined)}
                    </td>
                    <td>{renderContainerSize(j)}</td>
                    <td>{toUpper(j.icd || '') || '-'}</td>
                    <td>{renderDangerousClass(j)}</td>
                    <td>{renderIcdNominationCell(j)}</td>
                    <td>{renderAttachmentCell(j, 'AUTHORIZATION', 'Authorization Letter')}</td>
                    <td>{toUpper(j.bl_type) || '-'}</td>
                    <td>{j.do_statuses && j.do_statuses.length > 0 ? (<span style={{ fontSize: 11 }}>{j.do_statuses.join(' / ')}</span>) : '-'}</td>
                    <td>{j.sl_user_initials || '-'}</td>
                    <td>{fmtDateShort(j.manifest_ready_date) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>

        </>
      )}

      {activeTab === 'pending_reports' && (
        <>
          <div style={{ fontWeight: 'bold', background: '#ffe6e6', padding: 8, borderRadius: 6, marginBottom: 8, marginTop: 16 }}>
            PENDING REPORTS
          </div>
          <TableScrollContainer>
            <table className="jobs-table" style={{ overflow: 'visible' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>IDF NO</th>
                  <th>PENDING FROM</th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>VESSEL</span><br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETA</span><br /><span style={{ fontWeight: 'bold' }}>ATA</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETB</span><br /><span style={{ fontWeight: 'bold' }}>ATB</span></th>
                  <th>SHIPPING LINE</th>
                  <th style={{ fontWeight: 'bold' }}>CUSTOMER NAME</th>
                  <th>CLIENT REF</th>
                  <th>BL</th>
                  <th>CONTAINER SIZE</th>
                  <th>ICD</th>
                  <th>DANGEROUS CLASS</th>
                  <th>ICD NOMINATION</th>
                  <th>LETTER</th>
                  <th>BL TYPE</th>
                  <th>DO STATUS</th>
                  <th>SL USER</th>
                  <th>MANIFEST OUT</th>
                </tr>
              </thead>
              <tbody>
                {pendingLoading && (<tr><td colSpan={19} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
                {!pendingLoading && filteredPendingJobs.length === 0 && (<tr><td colSpan={19} className="no-data">No pending records found.</td></tr>)}
                {!pendingLoading && filteredPendingJobs.map(j => (
                  <tr key={j.id} className={j.manifest_ready_date ? 'flash-orange' : ''}>
                    <td><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openJobFormModal(j); }}>{j.id}</a></td>
                    <td>{j.idf_no || '-'}</td>
                    <td>{fmtDateShort(j.pending_from) || '-'}</td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>{toUpper(j.vessel)}</span><br /><span style={{ fontWeight: 'normal' }}>{toUpper(j.voyage_number)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.eta)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.ata)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.etb)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.atb)}</span></td>
                    <td>{toUpper(j.shipping_line_name || j.shipping_line || '') || '-'}</td>
                    <td style={{ fontWeight: 'bold' }}>{toUpper(j.customer_name)}</td>
                    <td>{j.client_ref || '-'}</td>
                    {/* 7. Conditionally render the BL link in the pending section */}
                    <td>
                        {renderBlNumber(j, hasPermission('SHIPPING REPORTS', 'can_edit') ? handleBlClick : undefined)}
                    </td>
                    <td>{renderContainerSize(j)}</td>
                    <td>{toUpper(j.icd || '') || '-'}</td>
                    <td>{renderDangerousClass(j)}</td>
                    <td>{renderIcdNominationCell(j)}</td>
                    <td>{renderAttachmentCell(j, 'AUTHORIZATION', 'Authorization Letter')}</td>
                    <td>{toUpper(j.bl_type) || '-'}</td>
                    <td>{j.do_statuses && j.do_statuses.length > 0 ? (<span style={{ fontSize: 11 }}>{j.do_statuses.join(' / ')}</span>) : '-'}</td>
                    <td>{j.sl_user_initials || '-'}</td>
                    <td>{fmtDateShort(j.manifest_ready_date) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>
        </>
      )}

      {activeTab === 'do_received' && (() => {
        const doReceivedTotalPages = Math.max(1, Math.ceil(doReceivedJobs.length / doReceivedPageSize));
        const doReceivedPageClamped = Math.min(doReceivedPage, doReceivedTotalPages);
        const doReceivedStart = (doReceivedPageClamped - 1) * doReceivedPageSize;
        const doReceivedPagedJobs = doReceivedJobs.slice(doReceivedStart, doReceivedStart + doReceivedPageSize);
        return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#eef5ff', padding: '8px 12px', borderRadius: 6, marginBottom: 8, marginTop: 16 }}>
            <div style={{ fontWeight: 'bold' }}>DO RECEIVED REPORT
              {!appliedSearch.term && (
                <span style={{ fontWeight: 'normal', fontSize: 12, marginLeft: 10, color: '#555' }}>(Showing last 2 months — search to see all)</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <span style={{ color: '#555' }}>Rows per page:</span>
              {[25, 50, 100].map(size => (
                <button key={size} onClick={() => { setDoReceivedPageSize(size); setDoReceivedPage(1); }}
                  style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: 'pointer', fontWeight: doReceivedPageSize === size ? 'bold' : 'normal', background: doReceivedPageSize === size ? '#007bff' : '#fff', color: doReceivedPageSize === size ? '#fff' : '#333' }}>
                  {size}
                </button>
              ))}
            </div>
          </div>
          <TableScrollContainer>
            <table className="jobs-table" style={{ overflow: 'visible' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>IDF NO</th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>VESSEL</span><br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETA</span><br /><span style={{ fontWeight: 'bold' }}>ATA</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETB</span><br /><span style={{ fontWeight: 'bold' }}>ATB</span></th>
                  <th>SHIPPING LINE</th>
                  <th style={{ fontWeight: 'bold' }}>CUSTOMER NAME</th>
                  <th>CLIENT REF</th>
                  <th>BL</th>
                  <th>CONTAINER SIZE</th>
                  <th>ICD</th>
                  <th>DO REC'D DATE</th>
                  <th>DO EXP DATE</th>
                  <th>DROP OFF EXP DATE</th>                  
                  <th>DROP OFF LOCATION</th>
                  <th>DO ATTACHMENT</th>
                  <th>DROP OFF ATTACHMENT</th>
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={17} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
                {!loading && doReceivedJobs.length === 0 && (<tr><td colSpan={17} className="no-data">No records with DO status "DO RECEIVED" found.</td></tr>)}
                {!loading && doReceivedPagedJobs.map(j => (
                  <tr key={j.id}>
                    <td><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openJobFormModal(j); }}>{j.id}</a></td>
                    <td>{j.idf_no || '-'}</td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>{toUpper(j.vessel)}</span><br /><span style={{ fontWeight: 'normal' }}>{toUpper(j.voyage_number)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.eta)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.ata)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.etb)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.atb)}</span></td>
                    <td>{toUpper(j.shipping_line_name || j.shipping_line || '') || '-'}</td>
                    <td style={{ fontWeight: 'bold' }}>{toUpper(j.customer_name)}</td>
                    <td>{j.client_ref || '-'}</td>
                    <td>{renderBlNumber(j, hasPermission('SHIPPING REPORTS', 'can_edit') ? handleBlClick : undefined)}</td>
                    <td>{renderContainerSize(j)}</td>
                    <td>{toUpper(j.icd || '') || '-'}</td>
                    <td>{fmtDateShort(j.do_received_date)}</td>
                    <td style={{ color: 'red' }}>{fmtDateShort(j.do_expiry_date)}</td>
                    <td className={isExpiringSoon(j.drop_off_expiry_date) && !isReleasedJob(j) ? 'flash-red' : ''} title={isExpiringSoon(j.drop_off_expiry_date) && !isReleasedJob(j) ? 'Renew Dropoff' : undefined} style={{ color: 'red' }}>{fmtDateShort(j.drop_off_expiry_date)}</td>
                    <td>{j.drop_off_location_display || j.drop_off_location || '-'}</td>
                    <td>{renderAttachmentCell(j, 'DO ATTACHMENT', 'DO Attachment')}</td>
                    <td>{renderAttachmentCell(j, 'DROPOFF ATTACHMENT', 'Drop Off Attachment')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>
          {doReceivedTotalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, marginTop: 8, fontSize: 13 }}>
              <span style={{ color: '#555' }}>
                {doReceivedStart + 1}–{Math.min(doReceivedStart + doReceivedPageSize, doReceivedJobs.length)} of {doReceivedJobs.length}
              </span>
              <button onClick={() => setDoReceivedPage(p => Math.max(1, p - 1))} disabled={doReceivedPageClamped === 1}
                style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: doReceivedPageClamped === 1 ? 'default' : 'pointer', background: '#fff', color: doReceivedPageClamped === 1 ? '#bbb' : '#333' }}>
                ‹ Prev
              </button>
              <span style={{ color: '#555' }}>Page {doReceivedPageClamped} / {doReceivedTotalPages}</span>
              <button onClick={() => setDoReceivedPage(p => Math.min(doReceivedTotalPages, p + 1))} disabled={doReceivedPageClamped === doReceivedTotalPages}
                style={{ padding: '2px 10px', borderRadius: 4, border: '1px solid #bbb', cursor: doReceivedPageClamped === doReceivedTotalPages ? 'default' : 'pointer', background: '#fff', color: doReceivedPageClamped === doReceivedTotalPages ? '#bbb' : '#333' }}>
                Next ›
              </button>
            </div>
          )}
        </>
        );
      })()}

      {activeTab === 'do_received_awaiting_dropoff' && (
        <>
          <div style={{ fontWeight: 'bold', background: '#fff6e8', padding: 8, borderRadius: 6, marginBottom: 8, marginTop: 16 }}>
            DO RECEIVED, AWAITING DROPOFF
          </div>
          <TableScrollContainer>
            <table className="jobs-table" style={{ overflow: 'visible' }}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>IDF NO</th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>VESSEL</span><br /><span style={{ fontWeight: 'normal' }}>VOYAGE</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETA</span><br /><span style={{ fontWeight: 'bold' }}>ATA</span></th>
                  <th style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>ETB</span><br /><span style={{ fontWeight: 'bold' }}>ATB</span></th>
                  <th>SHIPPING LINE</th>
                  <th style={{ fontWeight: 'bold' }}>CUSTOMER NAME</th>
                  <th>CLIENT REF</th>
                  <th>BL</th>
                  <th>CONTAINER SIZE</th>
                  <th>ICD</th>
                  <th>DO REC'D DATE</th>
                  <th>DO EXP DATE</th>
                  <th>DO ATTACHMENT</th>
                </tr>
              </thead>
              <tbody>
                {loading && (<tr><td colSpan={14} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
                {!loading && doReceivedAwaitingDropoffJobs.length === 0 && (<tr><td colSpan={14} className="no-data">No records with DO status "DO RECEIVED, AWAITING DROP-OFF" found.</td></tr>)}
                {!loading && doReceivedAwaitingDropoffJobs.map(j => (
                  <tr key={j.id}>
                    <td><a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openJobFormModal(j); }}>{j.id}</a></td>
                    <td>{j.idf_no || '-'}</td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'bold' }}>{toUpper(j.vessel)}</span><br /><span style={{ fontWeight: 'normal' }}>{toUpper(j.voyage_number)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.eta)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.ata)}</span></td>
                    <td style={{ whiteSpace: 'pre-line' }}><span style={{ fontWeight: 'normal' }}>{fmtDateShort(j.etb)}</span><br /><span style={{ fontWeight: 'bold' }}>{fmtDateShort(j.atb)}</span></td>
                    <td>{toUpper(j.shipping_line_name || j.shipping_line || '') || '-'}</td>
                    <td style={{ fontWeight: 'bold' }}>{toUpper(j.customer_name)}</td>
                    <td>{j.client_ref || '-'}</td>
                    <td>{renderBlNumber(j, hasPermission('SHIPPING REPORTS', 'can_edit') ? handleBlClick : undefined)}</td>
                    <td>{renderContainerSize(j)}</td>
                    <td>{toUpper(j.icd || '') || '-'}</td>
                    <td>{fmtDateShort(j.do_received_date)}</td>
                    <td>{fmtDateShort(j.do_expiry_date)}</td>
                    <td>{renderAttachmentCell(j, 'DO ATTACHMENT', 'DO Attachment')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScrollContainer>
        </>
      )}

      {activeTab === 'consignee_nomination' && (
          <>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#eef5ff', padding: '8px 16px', borderRadius: 6, marginBottom: 8, marginTop: 16, flexWrap: 'wrap' }}>
                 <div style={{ fontWeight: 'bold' }}>CONSIGNEE NOMINATION REPORT</div>
                 <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                     {(() => {
                         const allIds = NOMINATION_STATUS_OPTIONS.map(o => o.id);
                         const isAllSelected = allIds.every(id => nominationStatusFilter.includes(id));
                         const cards = [
                             ...NOMINATION_STATUS_OPTIONS.map(o => ({ ...o, color: NOMINATION_STATUS_COLORS[o.id] })),
                             { id: 'ALL', text: 'ALL', color: NOMINATION_STATUS_COLORS.ALL },
                         ];
                         return cards.map(card => {
                             const isSelected = card.id === 'ALL' ? isAllSelected : (!isAllSelected && nominationStatusFilter.includes(card.id));
                             const count = card.id === 'ALL' ? nominationStatusCounts.ALL : (nominationStatusCounts[card.id] || 0);
                             return (
                                 <div
                                     key={card.id}
                                     onClick={() => handleNominationCardClick(card.id)}
                                     style={{
                                         padding: '4px 10px',
                                         borderRadius: 12,
                                         background: isSelected ? card.color : '#fff',
                                         color: isSelected ? '#fff' : card.color,
                                         border: `2px solid ${card.color}`,
                                         cursor: 'pointer',
                                         fontSize: 12,
                                         fontWeight: 'bold',
                                         display: 'flex',
                                         alignItems: 'center',
                                         gap: 4,
                                         whiteSpace: 'nowrap',
                                     }}
                                 >
                                     {card.text} <span style={{ opacity: 0.9 }}>({count})</span>
                                 </div>
                             );
                         });
                     })()}
                 </div>
             </div>
             <TableScrollContainer>
                 <table className="jobs-table" style={{ overflow: 'visible' }}>
                     <thead>
                         <tr>
                             <th>ID</th>
                             <th>IDF NO.</th>
                             <th>BL NO.</th>
                             <th>CUSTOMER NAME</th>
                             <th>
                                 <div>SHIPPING LINE</div>
                                 <div>VESSEL NAME</div>
                             </th>
                             <th>ETA</th>
                             <th>NOMINATED ICD</th>
                             <th>FOLDER NAME</th>
                             <th>LETTER SUBMITTED</th>
                             <th>DATE SUBMITTED</th>
                             <th>ACCEPTED STATUS</th>
                         </tr>
                     </thead>
                     <tbody>
                         {nominationLoading && (<tr><td colSpan={11} style={{ textAlign: 'center' }}>Loading...</td></tr>)}
                         {!nominationLoading && filteredNominationJobs.length === 0 && (<tr><td colSpan={11} className="no-data">No records found.</td></tr>)}
                         {!nominationLoading && filteredNominationJobs.map(job => (
                             <tr key={job.id}>
                                 <td>
                                     {job.is_nomination ? (
                                         <span title="Pre-job nomination (awaiting job creation)" style={{ color: '#e67e22', fontWeight: 'bold' }}>{String(job.id).toUpperCase()}</span>
                                     ) : (
                                         <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openJobFormModal(job); }}>{job.id}</a>
                                     )}
                                 </td>
                                 <td>{job.idf_no || '-'}</td>
                                 <td>{job.bl_no || '-'}</td>
                                 <td><strong>{job.customer_name}</strong></td>
                                 <td>
                                     {job.shipping_line_name && <div style={{ fontWeight: 'bold' }}>{job.shipping_line_name}</div>}
                                     <div>{job.vessel_name || (!job.shipping_line_name ? '-' : '')}</div>
                                 </td>
                                 <td>{fmtDateShort(job.eta)}</td>
                                 <td>{job.nominated_icd || '-'}</td>
                                 <td>{job.folder_name || '-'}</td>
                                 <td style={{ textAlign: 'center' }}>
                                    {job.letter_submitted === 'YES' ? (
                                        <span style={{ fontWeight: 'bold', textDecoration: 'underline', color: '#007bff', cursor: 'default' }}>VIEW</span>
                                    ) : (
                                        <span>-</span>
                                    )}
                                 </td>
                                 <td>{fmtDateShort(job.nominated_by_updated_at)}</td>
                                 <td>
                                     <Select2Dropdown
                                         options={NOMINATION_STATUS_OPTIONS}
                                         value={job.nomination_status || ''}
                                         onChange={(val) => handleNominationStatusChange(job.id, val)}
                                         valueKey="id"
                                         displayKey="text"
                                         placeholder="PENDING"
                                         disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                                     />
                                 </td>
                             </tr>
                         ))}
                     </tbody>
                 </table>
             </TableScrollContainer>
          </>
      )}

      {activeTab === 'deposits' && (
        <DepositReport onRowsLoaded={setDeposits} />
      )}

      {showSwapDialog && (
        <div className="modal-overlay" onClick={resetSwapDialog}>
          <div className="dialog-container" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Swap SL Users</h3>
            </div>
            <div className="dialog-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>From User</label>
                  <Select2Dropdown
                    options={shippingUserOptions}
                    value={swapFromUser}
                    onChange={setSwapFromUser}
                    valueKey="id"
                    displayKey="text"
                    placeholder="Select Shipping Line user"
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>To User</label>
                  <Select2Dropdown
                    options={shippingUserOptions}
                    value={swapToUser}
                    onChange={setSwapToUser}
                    valueKey="id"
                    displayKey="text"
                    placeholder="Select Shipping Line user"
                  />
                </div>
              </div>
            </div>
            <div className="dialog-footer">
              <button type="button" className="dialog-btn dialog-btn-secondary" onClick={resetSwapDialog} disabled={swappingUsers}>
                Cancel
              </button>
              <button type="button" className="dialog-btn dialog-btn-primary" onClick={handleSwapUsers} disabled={swappingUsers}>
                {swappingUsers ? 'Swapping...' : 'Swap'}
              </button>
            </div>
          </div>
        </div>
      )}

      {shippingDetailsDialog.open && (
        <div className="modal-overlay">
          <div className="dialog-container" style={{ width: 650, maxHeight: '90vh', overflowY: 'auto', position: 'relative', margin: 'auto' }}>
            <div className="dialog-header">
                <h3>Shipping Details (IDF: {shippingDetailsDialog.job?.idf_no || 'N/A'})</h3>
            </div>
            <div className="dialog-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Authorization Letter</label>
                  {authLetterFor(shippingDetailsDialog.job) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => {
                          const letter = authLetterFor(shippingDetailsDialog.job);
                          const url = letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || '';
                          if (url) openFilePreview(url, 'Authorization Letter');
                      }} >View Existing Letter</button>
                      <input type="file" onChange={e => {
                          const file = e.target.files[0];
                          setShippingDetailsDialog(d => {
                            if (d.letterAttachmentPreviewUrl) URL.revokeObjectURL(d.letterAttachmentPreviewUrl);
                            return { ...d, letterFile: file, letterAttachmentPreviewUrl: file ? URL.createObjectURL(file) : '' };
                          });
                        }} disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                      {shippingDetailsDialog.letterAttachmentPreviewUrl && (
                        <button
                          type="button"
                          className="dialog-btn dialog-btn-primary dialog-btn-sm"
                          onClick={() => openFilePreview(shippingDetailsDialog.letterAttachmentPreviewUrl, shippingDetailsDialog.letterFile?.name || 'Selected Authorization Letter')}
                        >
                          View Selected Letter
                        </button>
                      )}
                    </div>
                  ) : (<>
                    <input type="file" onChange={e => {
                          const file = e.target.files[0];
                          setShippingDetailsDialog(d => {
                            if (d.letterAttachmentPreviewUrl) URL.revokeObjectURL(d.letterAttachmentPreviewUrl);
                            return { ...d, letterFile: file, letterAttachmentPreviewUrl: file ? URL.createObjectURL(file) : '' };
                          });
                        }} disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                    {shippingDetailsDialog.letterAttachmentPreviewUrl && (
                      <button
                        type="button"
                        className="dialog-btn dialog-btn-primary dialog-btn-sm"
                        onClick={() => openFilePreview(shippingDetailsDialog.letterAttachmentPreviewUrl, shippingDetailsDialog.letterFile?.name || 'Selected Authorization Letter')}
                      >
                        View Selected Letter
                      </button>
                    )}
                  </>)}
                </div>
                <div>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>BL Type</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 140 }}>
                      <Select2Dropdown options={BL_TYPE_OPTIONS.map(o => ({ id: o, text: o }))} value={shippingDetailsDialog.tempBLType} onChange={val => setShippingDetailsDialog(d => ({ ...d, tempBLType: val }))} valueKey="id" displayKey="text" placeholder="Select BL Type" isDisabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                    </div>
                    <a
                      href="#"
                      onClick={e => { e.preventDefault(); viewBlDocument(); }}
                      style={{ color: '#0d6efd', cursor: blLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap', fontSize: '0.85em' }}
                      title="View the BL document for this job"
                    >
                      {blLoading ? 'Loading…' : 'View BL'}
                    </a>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <label htmlFor="shipping-free-days" style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Free Days</label>
                      <input
                        type="number"
                        id="shipping-free-days"
                        min="0"
                        max="99"
                        className="search-input"
                        value={shippingDetailsDialog.tempFreeDays ?? ''}
                        onChange={e => setShippingDetailsDialog(d => ({ ...d, tempFreeDays: e.target.value }))}
                        disabled={!(hasPermission('SHIPPING REPORTS', 'can_delete') || (canEditShippingReport && !shippingDetailsDialog.job?.confirm_free_days))}
                        style={{ width: 45, minWidth: 0, padding: '6px 2px', textAlign: 'center' }}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', cursor: (hasPermission('SHIPPING REPORTS', 'can_delete') || !shippingDetailsDialog.job?.confirm_free_days) ? 'pointer' : 'not-allowed' }}>
                        <input
                          type="checkbox"
                          checked={!!shippingDetailsDialog.tempConfirmFreeDays}
                          onChange={e => handleConfirmFreeDaysChange(e.target.checked)}
                          disabled={!hasPermission('SHIPPING REPORTS', 'can_delete') && !!shippingDetailsDialog.job?.confirm_free_days}
                        />
                        Confirm Free Days
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>SL User</label>
                  <Select2Dropdown options={shippingStaff.map(s => ({ id: s.id, text: s.name }))} value={shippingDetailsDialog.tempSLUserId} onChange={val => setShippingDetailsDialog(d => ({ ...d, tempSLUserId: val }))} valueKey="id" displayKey="text" placeholder="Select SL User" isDisabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                </div>
                <div>
                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Status</label>
                  <MultiSelectDropdown id={`do-status-dialog-${shippingDetailsDialog.job?.id}`} options={dialogDoStatusOptions} selectedValues={shippingDetailsDialog.tempDOStatuses} onChange={handleDoStatusChange} placeholder="Select DO Status" maxMenuHeight={220} isDisabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                </div>

                {hasDoDetailStatusSelected && (
                  <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <h4 style={{ marginTop: 0, marginBottom: 8, color: '#333' }}>DO Received Details</h4>

                    {/* ── History: existing read-only DO records ── */}
                    {(() => {
                      const job = shippingDetailsDialog.job;
                      const dbRecords = Array.isArray(job?.do_records) ? job.do_records : [];

                      // Build display list.
                      // If do_records is empty but flat-field data exists, show a virtual "Original Entry"
                      // card so the user can see existing data before the first new POST (which triggers
                      // auto-migration into a real db record).
                      // Once do_records are populated, show them directly — the zeroth record IS the original.
                      let displayRecords = [...dbRecords];
                      const { origDoDate, origDoExpiry, origDropDate, origDropExpiry, origDropLocation } = shippingDetailsDialog;
                      if (dbRecords.length === 0 && origDoDate) {
                        const doLetter = findLetterByType(job, 'DO ATTACHMENT');
                        const dropLetter = findLetterByType(job, 'DROPOFF ATTACHMENT');
                        displayRecords = [{
                          id: 'original',
                          _virtual: true,
                          do_received_date: origDoDate,
                          do_expiry_date: origDoExpiry || null,
                          drop_off_date: origDropDate || null,
                          drop_off_expiry_date: origDropExpiry || null,
                          drop_off_location: origDropLocation || null,
                          do_attachment_url: doLetter ? (doLetter.generated_file || doLetter.file || doLetter.document || doLetter.url || doLetter.file_url || null) : null,
                          drop_off_attachment_url: dropLetter ? (dropLetter.generated_file || dropLetter.file || dropLetter.document || dropLetter.url || dropLetter.file_url || null) : null,
                          recorded_by_username: null,
                        }];
                      }

                      if (displayRecords.length === 0) return null;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ fontWeight: 'bold', fontSize: 12, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>
                            DO History ({displayRecords.length} {displayRecords.length === 1 ? 'entry' : 'entries'})
                          </div>
                          {displayRecords.map((rec, idx) => {
                            const isOriginal = rec._virtual || (idx === 0 && !rec.recorded_by_username);
                            return (
                            <div key={rec.id || idx} style={{ background: isOriginal ? '#f4f8ff' : '#f0f8f0', border: `1px solid ${isOriginal ? '#c5d8f5' : '#b2d8b2'}`, borderRadius: 6, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                <span style={{ fontWeight: 'bold', fontSize: 12, color: isOriginal ? '#555' : '#1a5fb4' }}>
                                  {isOriginal ? 'Original Entry' : `Entry #${idx + 1}`}
                                </span>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                  {rec.recorded_by_username && (
                                    <span style={{ fontSize: 11, color: '#888' }}>By: {rec.recorded_by_username}</span>
                                  )}
                                  {!isOriginal && rec.created_at && (
                                    <span style={{ fontSize: 11, color: '#888' }}>Saved: {fmtDateShort(rec.created_at)}</span>
                                  )}
                                  {rec.id && hasPermission('SHIPPING REPORTS', 'can_delete') && editingDoRecord !== rec.id && (
                                    <>
                                      <button type="button" className="icon-btn" title="Edit" onClick={() => startEditDoRecord(rec)} style={{ color: '#1a5fb4' }}>
                                        <i className="fa fa-pencil" />
                                      </button>
                                      {!rec._virtual && (
                                        <button type="button" className="icon-btn" title="Delete" onClick={() => openDeleteDoRecordModal(rec)} style={{ color: '#c62828' }}>
                                          <i className="fa fa-trash" />
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {editingDoRecord === rec.id && (
                                    <>
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => saveDoRecordEdit(rec)} disabled={doRecordSaving}>
                                        {doRecordSaving ? 'Saving...' : 'Save'}
                                      </button>
                                      <button type="button" className="dialog-btn dialog-btn-secondary dialog-btn-sm" onClick={cancelEditDoRecord} disabled={doRecordSaving}>
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              {editingDoRecord === rec.id ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                                    <div>
                                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>DO Received Date</label>
                                      <input type="date" className="search-input" value={doRecordEditDraft.do_received_date || ''} onChange={e => updateDoRecordDraft('do_received_date', e.target.value)} />
                                    </div>
                                    <div>
                                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>DO Expiry Date</label>
                                      <input type="date" className="search-input" value={doRecordEditDraft.do_expiry_date || ''} onChange={e => updateDoRecordDraft('do_expiry_date', e.target.value)} min={doRecordEditDraft.do_received_date || undefined} />
                                    </div>
                                  </div>
                                  {showDropoffFields && (
                                    <>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                                        <div>
                                          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Received Date</label>
                                          <input type="date" className="search-input" value={doRecordEditDraft.drop_off_date || ''} onChange={e => updateDoRecordDraft('drop_off_date', e.target.value)} />
                                        </div>
                                        <div>
                                          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Expiry Date</label>
                                          {(shippingDetailsDialog.job?.containers || []).filter(c => c.container_no).length > 1 ? (
                                            <a
                                              href="#"
                                              onClick={e => { e.preventDefault(); openDropoffExpiryModal(); }}
                                              title={getDropoffExpiryContainerTitle(shippingDetailsDialog.job.containers)}
                                              style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                            >
                                              {fmtDateShort(computeEarliestDropoffExpiry(shippingDetailsDialog.job.containers)) || 'Enter Date'}
                                            </a>
                                          ) : (
                                            <input type="date" className="search-input" value={doRecordEditDraft.drop_off_expiry_date || ''} onChange={e => updateDoRecordDraft('drop_off_expiry_date', e.target.value)} min={doRecordEditDraft.drop_off_date || undefined} />
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Location</label>
                                        <Select2Dropdown
                                          options={dropOffLocations.map(loc => ({ id: loc.name, text: loc.display_name }))}
                                          value={doRecordEditDraft.drop_off_location || ''}
                                          onChange={val => updateDoRecordDraft('drop_off_location', val)}
                                          valueKey="id"
                                          displayKey="text"
                                          placeholder="Select or type location"
                                          tags={true}
                                          isDisabled={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                                        />
                                      </div>
                                    </>
                                  )}
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 200 }}>
                                      <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>DO Attachment</label>
                                      {rec.do_attachment_url && (
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                                          <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.do_attachment_url, 'DO Attachment')}>View Current</button>
                                          <span style={{ fontSize: 11, color: '#888' }}>Replace:</span>
                                        </div>
                                      )}
                                      <input type="file" onChange={e => updateDoRecordDraft('doAttachmentFile', e.target.files[0])} />
                                    </div>
                                    {showDropoffFields && (
                                      <div style={{ flex: 1, minWidth: 200 }}>
                                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4, fontSize: 12 }}>Drop-Off Attachment</label>
                                        {rec.drop_off_attachment_url && (
                                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                                            <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.drop_off_attachment_url, 'Drop-Off Attachment')}>View Current</button>
                                            <span style={{ fontSize: 11, color: '#888' }}>Replace:</span>
                                          </div>
                                        )}
                                        <input type="file" onChange={e => updateDoRecordDraft('dropOffAttachmentFile', e.target.files[0])} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 12 }}>
                                    <div><span style={{ color: '#555' }}>DO Received:</span> <strong>{fmtDateShort(rec.do_received_date) || '—'}</strong></div>
                                    <div><span style={{ color: '#555' }}>DO Expiry:</span> <strong>{fmtDateShort(rec.do_expiry_date) || '—'}</strong></div>
                                    {showDropoffFields && (
                                      <>
                                        <div><span style={{ color: '#555' }}>Drop-Off Received:</span> <strong>{fmtDateShort(rec.drop_off_date) || '—'}</strong></div>
                                        <div>
                                          <span style={{ color: '#555' }}>Drop-Off Expiry:</span>{' '}
                                          {idx === displayRecords.length - 1 && (job?.containers || []).filter(c => c.container_no).length > 1 ? (
                                            <strong>
                                              <span
                                                title={getDropoffExpiryContainerTitle(job.containers)}
                                                onClick={() => hasPermission('SHIPPING REPORTS', 'can_delete') && openDropoffExpiryModal('direct')}
                                                style={hasPermission('SHIPPING REPORTS', 'can_delete') ? { color: '#1a5fb4', textDecoration: 'underline', cursor: 'pointer' } : {}}
                                              >
                                                {fmtDateShort(rec.drop_off_expiry_date) || '—'}
                                              </span>
                                              {hasMultipleDropoffExpiries(job.containers) && <span style={{ color: 'red', marginLeft: 6 }}>(Multiple)</span>}
                                            </strong>
                                          ) : (
                                            <strong>
                                              {fmtDateShort(rec.drop_off_expiry_date) || '—'}
                                              {hasMultipleDropoffExpiries(job.containers) && <span style={{ color: 'red', marginLeft: 6 }}>(Multiple)</span>}
                                            </strong>
                                          )}
                                        </div>
                                        {rec.drop_off_location && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#555' }}>Location:</span> <strong>{rec.drop_off_location}</strong></div>}
                                      </>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                    {rec.do_attachment_url && (
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.do_attachment_url, 'DO Attachment')}>View DO Attachment</button>
                                    )}
                                    {rec.drop_off_attachment_url && (
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(rec.drop_off_attachment_url, 'Drop-Off Attachment')}>View Drop-Off Attachment</button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* ── First-time entry OR Add New Entry ── */}
                    {(() => {
                      const job = shippingDetailsDialog.job;
                      const existingRecords = Array.isArray(job?.do_records) ? job.do_records : [];
                      // Use origDoDate (frozen at dialog open) so hasHistory stays stable
                      const hasHistory = existingRecords.length > 0 || !!(shippingDetailsDialog.origDoDate);
                      const setNewDo = updater => setShippingDetailsDialog(d => ({
                        ...d,
                        newDoEntry: typeof updater === 'function' ? updater(d.newDoEntry) : updater
                      }));
                      const newDo = shippingDetailsDialog.newDoEntry || {};

                      // Determine which dates/fields to bind (legacy when closed, newDoEntry when open)
                      const doDateVal = newDo.isOpen ? (newDo.doReceivedDate || '') : shippingDetailsDialog.doReceivedDate;
                      const setDoDate = newDo.isOpen
                        ? (v => setNewDo(n => ({ ...n, doReceivedDate: v })))
                        : (v => setShippingDetailsDialog(d => ({ ...d, doReceivedDate: v })));

                      const doExpVal = newDo.isOpen ? (newDo.doExpiryDate || '') : shippingDetailsDialog.doExpiryDate;
                      const setDoExp = newDo.isOpen
                        ? (v => setNewDo(n => ({ ...n, doExpiryDate: v })))
                        : (v => setShippingDetailsDialog(d => ({ ...d, doExpiryDate: v })));

                      const dropDateVal = newDo.isOpen ? (newDo.dropOffDate || '') : shippingDetailsDialog.dropOffDate;
                      const setDropDate = newDo.isOpen
                        ? (v => setNewDo(n => ({ ...n, dropOffDate: v })))
                        : (v => setShippingDetailsDialog(d => ({ ...d, dropOffDate: v })));

                      const dropExpVal = newDo.isOpen ? (newDo.dropOffExpiryDate || '') : shippingDetailsDialog.dropOffExpiryDate;
                      const setDropExp = newDo.isOpen
                        ? (v => setNewDo(n => ({ ...n, dropOffExpiryDate: v })))
                        : (v => setShippingDetailsDialog(d => ({ ...d, dropOffExpiryDate: v })));

                      const dropLocVal = newDo.isOpen ? (newDo.dropOffLocation || '') : shippingDetailsDialog.dropOffLocation;
                      const setDropLoc = newDo.isOpen
                        ? (v => setNewDo(n => ({ ...n, dropOffLocation: v })))
                        : (v => setShippingDetailsDialog(d => ({ ...d, dropOffLocation: v })));

                      const setDoFile = newDo.isOpen
                        ? (f => setNewDo(n => {
                            if (n.doAttachmentPreviewUrl) URL.revokeObjectURL(n.doAttachmentPreviewUrl);
                            return { ...n, doAttachmentFile: f, doAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                          }))
                        : (f => setShippingDetailsDialog(d => {
                            if (d.doAttachmentPreviewUrl) URL.revokeObjectURL(d.doAttachmentPreviewUrl);
                            return { ...d, doAttachmentFile: f, doAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                          }));

                      const setDropFile = newDo.isOpen
                        ? (f => setNewDo(n => {
                            if (n.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(n.dropoffAttachmentPreviewUrl);
                            return { ...n, dropOffAttachmentFile: f, dropoffAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                          }))
                        : (f => setShippingDetailsDialog(d => {
                            if (d.dropoffAttachmentPreviewUrl) URL.revokeObjectURL(d.dropoffAttachmentPreviewUrl);
                            return { ...d, dropOffAttachmentFile: f, dropoffAttachmentPreviewUrl: f ? URL.createObjectURL(f) : '' };
                          }));

                      const doPreviewUrl = newDo.isOpen ? (newDo.doAttachmentPreviewUrl || '') : (shippingDetailsDialog.doAttachmentPreviewUrl || '');
                      const dropPreviewUrl = newDo.isOpen ? (newDo.dropoffAttachmentPreviewUrl || '') : (shippingDetailsDialog.dropoffAttachmentPreviewUrl || '');

                      // Resolve previous entry for "use previous" pre-fill display.
                      // Use the last real db record (backend auto-migrates legacy data on first POST).
                      // Fallback to frozen flat fields only if no db records exist yet (pre-first-POST).
                      const { origDoDate: _origDoDate, origDoExpiry: _origDoExpiry, origDropDate: _origDropDate, origDropExpiry: _origDropExpiry, origDropLocation: _origDropLocation } = shippingDetailsDialog;
                      const prevRecord = existingRecords.length > 0
                        ? existingRecords[existingRecords.length - 1]
                        : (_origDoDate ? {
                            do_received_date: _origDoDate,
                            do_expiry_date: _origDoExpiry,
                            drop_off_date: _origDropDate,
                            drop_off_expiry_date: _origDropExpiry,
                            drop_off_location: _origDropLocation,
                            do_attachment_url: (() => { const l = findLetterByType(job, 'DO ATTACHMENT'); return l ? (l.generated_file || l.file || l.document || l.url || l.file_url || null) : null; })(),
                            drop_off_attachment_url: (() => { const l = findLetterByType(job, 'DROPOFF ATTACHMENT'); return l ? (l.generated_file || l.file || l.document || l.url || l.file_url || null) : null; })(),
                          } : null);

                      const usePrevDO = newDo.usePreviousDO;
                      const usePrevDrop = newDo.usePreviousDropoff;

                      // When "use previous" is checked, display values come from prevRecord
                      const displayDoDate = usePrevDO ? (prevRecord?.do_received_date || '') : doDateVal;
                      const displayDoExp = usePrevDO ? (prevRecord?.do_expiry_date || '') : doExpVal;
                      const displayDropDate = usePrevDrop ? (prevRecord?.drop_off_date || '') : dropDateVal;
                      const displayDropExp = usePrevDrop ? (prevRecord?.drop_off_expiry_date || '') : dropExpVal;
                      const displayDropLoc = usePrevDrop ? (prevRecord?.drop_off_location || '') : dropLocVal;

                      if (!newDo.isOpen) {
                        return (
                          <div style={{ marginTop: 12 }}>
                            <button
                              type="button"
                              className="dialog-btn dialog-btn-primary"
                              disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                              onClick={() => setNewDo(n => ({
                                ...n,
                                isOpen: true,
                                doReceivedDate: formatDateForInput(prevRecord?.do_received_date) || n.doReceivedDate || '',
                                doExpiryDate: formatDateForInput(prevRecord?.do_expiry_date) || n.doExpiryDate || '',
                                dropOffDate: formatDateForInput(prevRecord?.drop_off_date) || n.dropOffDate || '',
                                dropOffExpiryDate: formatDateForInput(prevRecord?.drop_off_expiry_date) || n.dropOffExpiryDate || '',
                                dropOffLocation: prevRecord?.drop_off_location || n.dropOffLocation || ''
                              }))}
                            >
                              + Add New DO Entry
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div style={hasHistory ? { borderTop: '2px dashed #c5d8f5', paddingTop: 12, marginTop: 4 } : {}}>
                          {hasHistory && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                              <span style={{ fontWeight: 'bold', fontSize: 12, color: '#1a5fb4', textTransform: 'uppercase', letterSpacing: 1 }}>+ Add New DO Entry</span>
                              <button
                                type="button"
                                className="dialog-btn dialog-btn-secondary dialog-btn-sm"
                                onClick={() => setNewDo(n => ({ ...n, isOpen: false }))}
                              >
                                Cancel
                              </button>
                            </div>
                          )}

                          {/* ── DO Section ── */}
                          <div style={{ background: usePrevDO ? '#f0f6ff' : 'transparent', border: usePrevDO ? '1px solid #c5d8f5' : 'none', borderRadius: 4, padding: usePrevDO ? '10px 12px' : '0', marginBottom: 12 }}>
                            {hasHistory && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
                                <input
                                  type="checkbox"
                                  checked={!!usePrevDO}
                                  onChange={e => {
                                    const checked = e.target.checked;
                                    setNewDo(n => ({
                                      ...n,
                                      usePreviousDO: checked,
                                      doReceivedDate: !checked ? formatDateForInput(prevRecord?.do_received_date) : '',
                                      doExpiryDate: !checked ? formatDateForInput(prevRecord?.do_expiry_date) : '',
                                      doAttachmentFile: null,
                                      doAttachmentPreviewUrl: ''
                                    }));
                                  }}
                                  disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                                />
                                <span style={{ fontWeight: 'bold', color: '#1a5fb4' }}>Use Previous DO Details</span>
                                {usePrevDO && prevRecord?.do_received_date && (
                                  <span style={{ fontSize: 11, color: '#555', fontWeight: 'normal' }}>({fmtDateShort(prevRecord.do_received_date)})</span>
                                )}
                              </label>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                              <div>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Received Date {!usePrevDO && <span style={{ color: 'red' }}>*</span>}</label>
                                <input type="date" className="search-input" value={displayDoDate} max={new Date().toISOString().split('T')[0]}
                                  onChange={e => !usePrevDO && setDoDate(e.target.value)}
                                  readOnly={usePrevDO || !hasPermission('SHIPPING REPORTS', 'can_edit')}
                                  style={usePrevDO ? { background: '#e8f0fe', color: '#555' } : {}}
                                />
                              </div>
                              <div>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Expiry Date</label>
                                <input type="date" className="search-input" value={displayDoExp}
                                  onChange={e => !usePrevDO && setDoExp(e.target.value)}
                                  readOnly={usePrevDO || !hasPermission('SHIPPING REPORTS', 'can_edit')}
                                  style={usePrevDO ? { background: '#e8f0fe', color: '#555' } : {}}
                                  min={displayDoDate || undefined}
                                />
                              </div>
                            </div>
                            <div style={{ marginTop: 12 }}>
                              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>DO Attachment</label>
                              {usePrevDO ? (
                                prevRecord?.do_attachment_url ? (
                                  <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(prevRecord.do_attachment_url, 'DO Attachment (Previous)')}>
                                    View Previous Attachment
                                  </button>
                                ) : <span style={{ fontSize: 12, color: '#888' }}>No previous attachment</span>
                              ) : (
                                <>
                                  {!hasHistory && findLetterByType(shippingDetailsDialog.job, 'DO ATTACHMENT') && (
                                    <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" style={{ marginBottom: 6 }} onClick={() => {
                                      const letter = findLetterByType(shippingDetailsDialog.job, 'DO ATTACHMENT');
                                      const url = letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || '';
                                      if (url) openFilePreview(url, 'DO Attachment');
                                    }}>View Current Attachment</button>
                                  )}
                                  <input type="file" onChange={e => setDoFile(e.target.files[0])} disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                                  {doPreviewUrl && (
                                    <button
                                      type="button"
                                      className="dialog-btn dialog-btn-primary dialog-btn-sm"
                                      style={{ marginTop: 6 }}
                                      onClick={() => openFilePreview(doPreviewUrl, (hasHistory ? newDo.doAttachmentFile : shippingDetailsDialog.doAttachmentFile)?.name || 'DO Attachment (Selected)')}
                                    >
                                      View Selected Attachment
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          {/* ── Drop-Off Section ── */}
                          {showDropoffFields && (
                            <div style={{ background: usePrevDrop ? '#f0f6ff' : 'transparent', border: usePrevDrop ? '1px solid #c5d8f5' : 'none', borderRadius: 4, padding: usePrevDrop ? '10px 12px' : '0' }}>
                              {hasHistory && (
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
                                  <input
                                    type="checkbox"
                                    checked={!!usePrevDrop}
                                    onChange={e => {
                                      const checked = e.target.checked;
                                      setNewDo(n => ({
                                        ...n,
                                        usePreviousDropoff: checked,
                                        dropOffDate: !checked ? formatDateForInput(prevRecord?.drop_off_date) : '',
                                        dropOffExpiryDate: !checked ? formatDateForInput(prevRecord?.drop_off_expiry_date) : '',
                                        dropOffLocation: !checked ? (prevRecord?.drop_off_location || '') : '',
                                        dropOffAttachmentFile: null,
                                        dropoffAttachmentPreviewUrl: ''
                                      }));
                                    }}
                                    disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                                  />
                                  <span style={{ fontWeight: 'bold', color: '#1a5fb4' }}>Use Previous Drop-Off Details</span>
                                  {usePrevDrop && prevRecord?.drop_off_date && (
                                    <span style={{ fontSize: 11, color: '#555', fontWeight: 'normal' }}>({fmtDateShort(prevRecord.drop_off_date)})</span>
                                  )}
                                </label>
                              )}
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop-Off Received Date {!usePrevDrop && <span style={{ color: 'red' }}>*</span>}</label>
                                  <input type="date" className="search-input" value={displayDropDate} max={new Date().toISOString().split('T')[0]}
                                    onChange={e => !usePrevDrop && setDropDate(e.target.value)}
                                    readOnly={usePrevDrop || !hasPermission('SHIPPING REPORTS', 'can_edit')}
                                    style={usePrevDrop ? { background: '#e8f0fe', color: '#555' } : {}}
                                  />
                                </div>
                                <div>
                                  <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop-Off Expiry Date {!usePrevDrop && !hasHistory && <span style={{ color: 'red' }}>*</span>}</label>
                                  {usePrevDrop ? (
                                    <input type="date" className="search-input" value={displayDropExp} readOnly style={{ background: '#e8f0fe', color: '#555' }} />
                                  ) : ((job?.containers || []).filter(c => c.container_no).length > 1) ? (
                                    (hasHistory && !newDo.dropOffExpiryDate) ? (
                                      <a
                                        href="#"
                                        onClick={e => { e.preventDefault(); openDropoffExpiryModal('staged', 'newdo'); }}
                                        style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                      >
                                        Add Drop-Off Expiry
                                      </a>
                                    ) : (
                                      <a
                                        href="#"
                                        onClick={e => { e.preventDefault(); openDropoffExpiryModal('staged', 'newdo'); }}
                                        title={getDropoffExpiryContainerTitle(job.containers)}
                                        style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                      >
                                        {fmtDateShort(hasHistory ? newDo.dropOffExpiryDate : computeEarliestDropoffExpiry(job.containers)) || 'Enter Date'}
                                      </a>
                                    )
                                  ) : (
                                    (hasHistory && !newDo.showDropoffExpiry && !newDo.dropOffExpiryDate) ? (
                                      <a
                                        href="#"
                                        onClick={e => { e.preventDefault(); setNewDo(n => ({ ...n, showDropoffExpiry: true })); }}
                                        style={{ color: '#1a5fb4', fontWeight: 500, textDecoration: 'underline', cursor: 'pointer' }}
                                      >
                                        Add Drop-Off Expiry
                                      </a>
                                    ) : (
                                      <input type="date" className="search-input" value={displayDropExp}
                                        onChange={e => setDropExp(e.target.value)}
                                        readOnly={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                                        min={displayDropDate || undefined}
                                      />
                                    )
                                  )}
                                </div>
                              </div>
                              <div style={{ marginTop: 12 }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop-Off Location</label>
                                {usePrevDrop ? (
                                  <input type="text" className="search-input" value={displayDropLoc} readOnly style={{ background: '#e8f0fe', color: '#555' }} />
                                ) : (
                                  <Select2Dropdown options={dropOffLocations.map(loc => ({ id: loc.name, text: loc.display_name }))} value={dropLocVal} onChange={val => setDropLoc(val)} valueKey="id" displayKey="text" placeholder="Select or type location" tags={true} isDisabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                                )}
                              </div>
                              <div style={{ marginTop: 12 }}>
                                <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Drop Off Attachment {!usePrevDrop && <span style={{ color: 'red' }}>*</span>}</label>
                                {usePrevDrop ? (
                                  prevRecord?.drop_off_attachment_url ? (
                                    <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(prevRecord.drop_off_attachment_url, 'Drop-Off Attachment (Previous)')}>
                                      View Previous Attachment
                                    </button>
                                  ) : <span style={{ fontSize: 12, color: '#888' }}>No previous attachment</span>
                                ) : (
                                  <>
                                    {!hasHistory && findLetterByType(shippingDetailsDialog.job, 'DROPOFF ATTACHMENT') && (
                                      <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" style={{ marginBottom: 6 }} onClick={() => {
                                        const letter = findLetterByType(shippingDetailsDialog.job, 'DROPOFF ATTACHMENT');
                                        const url = letter.generated_file || letter.file || letter.document || letter.url || letter.file_url || '';
                                        if (url) openFilePreview(url, 'Drop Off Attachment');
                                      }}>View Current Attachment</button>
                                    )}
                                    <input type="file" onChange={e => setDropFile(e.target.files[0])} disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')} />
                                    {dropPreviewUrl && (
                                      <button
                                        type="button"
                                        className="dialog-btn dialog-btn-primary dialog-btn-sm"
                                        style={{ marginTop: 6 }}
                                        onClick={() => openFilePreview(dropPreviewUrl, (hasHistory ? newDo.dropOffAttachmentFile : shippingDetailsDialog.dropOffAttachmentFile)?.name || 'Drop-Off Attachment (Selected)')}
                                      >
                                        View Selected Attachment
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* ── Additional Letter section ── */}
                    {hasDoDetailStatusSelected && (
                      <div style={{ borderTop: '1px solid #eee', paddingTop: 12 }}>
                        {(() => {
                          const selectedType = (shippingDetailsDialog.additionalLetterType || '').trim();
                          const additionalLetter = selectedType
                            ? findLetterByType(shippingDetailsDialog.job, selectedType)
                            : findLatestAdditionalLetter(shippingDetailsDialog.job);
                          const additionalTypeLabel = (additionalLetter?.letter_type || additionalLetter?.type || '').toString().trim();
                          if (!additionalLetter) return null;
                          return (
                            <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                              <button
                                type="button"
                                className="dialog-btn dialog-btn-primary dialog-btn-sm"
                                onClick={() => {
                                  const url = additionalLetter.generated_file || additionalLetter.file || additionalLetter.document || additionalLetter.url || additionalLetter.file_url || '';
                                  if (url) openFilePreview(url, additionalTypeLabel || 'Additional Letter');
                                }}
                              >
                                View Additional Letter{additionalTypeLabel ? ` (${additionalTypeLabel})` : ''}
                              </button>
                            </div>
                          );
                        })()}
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Additional Letter Type</label>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="e.g. CLIENT INSTRUCTION"
                          value={shippingDetailsDialog.additionalLetterType || ''}
                          onChange={e => setShippingDetailsDialog(d => ({ ...d, additionalLetterType: e.target.value }))}
                          readOnly={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                        />
                        <label style={{ fontWeight: 'bold', display: 'block', margin: '10px 0 4px' }}>Additional Letter File</label>
                        <input
                          type="file"
                          onChange={e => setShippingDetailsDialog(d => ({ ...d, additionalLetterFile: e.target.files[0] }))}
                          disabled={!hasPermission('SHIPPING REPORTS', 'can_edit')}
                        />
                      </div>
                    )}
                  </div>
                )}             
              </div>
            </div>
            <div className="dialog-footer">
              <button type="button" className="dialog-btn dialog-btn-secondary" onClick={closeShippingDetailsDialog}>Cancel</button>
              {hasPermission('SHIPPING REPORTS', 'can_edit') && (
                <button type="button" className="dialog-btn dialog-btn-primary" disabled={shippingDetailsDialog.saving} onClick={saveShippingDetails}>{shippingDetailsDialog.saving ? 'Saving...' : 'Save'}</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Free Days prompt */}
      {showFreeDaysConfirm && (
        <div className="modal-overlay" onClick={() => setShowFreeDaysConfirm(false)}>
          <div className="dialog-container" style={{ width: 400, margin: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Confirm Free Days</h3>
            </div>
            <div className="dialog-body">
              <p>Have you confirmed Free Days with BL?</p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-secondary" onClick={() => setShowFreeDaysConfirm(false)}>Cancel</button>
              <button className="dialog-btn dialog-btn-primary" onClick={() => {
                setShippingDetailsDialog(d => ({ ...d, tempConfirmFreeDays: true }));
                setShowFreeDaysConfirm(false);
              }}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {/* Custom MD Alert Modal */}
      {showMDAlert && (
        <div
            className="modal-overlay"
            onClick={() => setShowMDAlert(false)}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.35)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <div
                className="modal-container"
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: '#fff',
                    borderRadius: 10,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 1.5px 4px rgba(0,0,0,0.12)',
                    padding: '32px 28px 24px 28px',
                    minWidth: 340,
                    maxWidth: 420,
                    border: '1px solid #e0e0e0',
                    fontFamily: 'inherit'
                }}
            >
                <div style={{
                    margin: '0 0 28px 0',
                    color: '#444',
                    fontSize: 15,
                    lineHeight: 1.6
                }}>
                    PLEASE INFORM MD
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                    <button
                        onClick={() => setShowMDAlert(false)}
                        style={{
                            padding: '7px 20px',
                            background: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 5,
                            fontSize: 15,
                            fontWeight: 500,
                            cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.07)'
                        }}
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Drop-Off Expiry Date per-container modal */}
      {showDropoffExpiryModal && (
        <div className="modal-overlay" onClick={closeDropoffExpiryModal}>
          <div className="dialog-container" style={{ width: 600, maxHeight: '80vh', overflowY: 'auto', margin: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Drop-Off Expiry Dates (IDF: {shippingDetailsDialog.job?.idf_no || 'N/A'})</h3>
            </div>
            <div className="dialog-body">
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', background: '#f4f4f4', padding: '10px', borderRadius: '4px' }}>
                <label style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Bulk Drop-Off Expiry</label>
                <input type="date" className="search-input" value={bulkDropoffExpiryDate} onChange={e => setBulkDropoffExpiryDate(e.target.value)} readOnly={!canEditDropoffExpiryModal} style={{ width: 150 }} />
                <button className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={applyBulkDropoffExpiry} disabled={!canEditDropoffExpiryModal || !bulkDropoffExpiryDate || selectedDropoffContainerIds.length === 0}>Apply to Selected</button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="jobs-table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" checked={selectedDropoffContainerIds.length === dropoffExpiryContainers.length && dropoffExpiryContainers.length > 0} disabled={!canEditDropoffExpiryModal} onChange={e => { if (e.target.checked) setSelectedDropoffContainerIds(dropoffExpiryContainers.map(c => c.id)); else setSelectedDropoffContainerIds([]); }} /></th>
                      <th>Container No</th>
                      <th>Drop-Off Expiry Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dropoffExpiryContainers.map(c => (
                      <tr key={c.id}>
                        <td><input type="checkbox" checked={selectedDropoffContainerIds.includes(c.id)} disabled={!canEditDropoffExpiryModal} onChange={() => handleDropoffExpirySelectionChange(c.id)} /></td>
                        <td>{c.container_no}</td>
                        <td><input type="date" className="search-input" value={c.drop_off_expiry_date || ''} onChange={e => handleDropoffExpiryContainerChange(c.id, e.target.value)} readOnly={!canEditDropoffExpiryModal} style={{ width: 150 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-secondary" onClick={closeDropoffExpiryModal}>Cancel</button>
              {canEditDropoffExpiryModal && <button className="dialog-btn dialog-btn-primary" onClick={saveDropoffExpiryChanges} disabled={savingDropoffExpiry}>{savingDropoffExpiry ? 'Saving...' : 'Save'}</button>}
            </div>
          </div>
        </div>
      )}

      {/* Delete DO record confirmation modal */}
      {deletingDoRecord && (
        <div className="modal-overlay" onClick={closeDeleteDoRecordModal}>
          <div className="dialog-container" style={{ width: 400, margin: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="dialog-body">
              <p>Are you sure you want to delete this DO history record?</p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn dialog-btn-secondary" onClick={closeDeleteDoRecordModal} disabled={doRecordSaving}>Cancel</button>
              <button className="dialog-btn dialog-btn-sm" onClick={doDeleteDoRecord} disabled={doRecordSaving} style={{ background: '#c62828', color: '#fff', borderColor: '#c62828' }}>{doRecordSaving ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* JobForm view modal */}
      {jobFormModal.isOpen && (
        <div className="modal-overlay" onClick={() => closeJobFormModal(false)}>
          <div
            className="dialog-container"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '95%',
              maxWidth: 1400,
              height: '95vh',
              display: 'flex',
              flexDirection: 'column',
              margin: 'auto',
            }}
          >
            <div className="dialog-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Job Details (View Only)</h3>
              <button className="icon-btn" onClick={() => closeJobFormModal(false)} title="Close">
                <i className="fa fa-times" />
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 20px 20px' }}>
              <JobForm
                mode="view"
                initialData={jobFormModal.job}
                onClose={closeJobFormModal}
                documentsEditable={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}